import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Ruler, Plus, Trash2, X, Pencil, Crop, RotateCw, Palette, Square, Droplet, GripVertical, Move } from 'lucide-react';
import RoomVisualizer3D from './RoomVisualizer3D';
import MaterialSearchField from './MaterialSearchField';
import MaterialLibraryManager from './MaterialLibraryManager';

const API_URL = (window.ENV?.REACT_APP_BACKEND_URL || process.env.REACT_APP_BACKEND_URL || window.location.origin);

/* ---------------------------------------------------------------------------
 * Palette squares for items with no photo.
 * Vendor images are often missing, and a grid of "No Image" boxes reads as
 * broken and gives nothing to recognise. These derive something usable from the
 * item itself: its finish colour when it names one, otherwise a stable colour
 * from its name plus its initials.
 * ------------------------------------------------------------------------- */

// Colour words that turn up in finish/colour fields on real sheets.
const COLOUR_WORDS = {
  white: '#ece9e4', alabaster: '#efece5', cream: '#f0e7d5', ivory: '#f2ece1',
  beige: '#ded3c0', sand: '#d8c9ae', taupe: '#b7a998', greige: '#c3bbae',
  grey: '#9a9a9a', gray: '#9a9a9a', charcoal: '#4a4a4d', graphite: '#53565a',
  black: '#2b2b2d', ebony: '#31302f',
  walnut: '#6b4a32', oak: '#c19a6b', wood: '#9c6f4a', natural: '#cbbba0',
  brass: '#c8a84e', gold: '#c9a227', bronze: '#8c6a3f', nickel: '#b6b8ba',
  chrome: '#c9ced2', steel: '#9fa6ad', copper: '#b87333',
  marble: '#e8e6e1', carrara: '#e6e5e0', quartz: '#e2ded7', quartzite: '#ded8cd',
  limestone: '#ddd3bf', travertine: '#d9c7a7', terrazzo: '#d5cfc4',
  green: '#4f6b52', emerald: '#2f6b4f', sage: '#9aa88f', olive: '#6b6b3f',
  blue: '#3f5a75', navy: '#2c3a4f', teal: '#2f6b6b',
  red: '#8a3f3f', terracotta: '#a5624a', rust: '#9c5b3b',
  pink: '#d8b3ac', blush: '#e0c3bb', mauve: '#a98f96',
  cognac: '#8a4f2d', camel: '#c19a6b', chocolate: '#4a352a',
};

/** True for a branded paint colour, where the name is a precise specification. */
function isPaint(item) {
  return /paint/i.test(`${item?.categoryName || ''} ${item?.category || ''}`)
    || /\b(SW|BM|F&B)\s?\d/i.test(item?.sku || '');
}

function swatchColorFor(item) {
  // A real hex always wins, wherever it came from.
  const hex = (item?.finish_color || '').match(/#[0-9a-f]{6}\b/i);
  if (hex) return hex[0];

  // Paint is deliberately excluded from guessing. The catalogue carries names
  // and codes but no hex values, and rendering "Coral Reef" as an inferred grey
  // would show a colour we do not actually know - worse than showing none, since
  // someone could specify from it. Paint gets a neutral chip with its code.
  if (isPaint(item)) return '#3a3a42';

  // For materials, the words in the finish field or the product name are
  // descriptive rather than a specification, so they are safe to read.
  const text = `${item?.finish_color || ''} ${item?.name || ''}`.toLowerCase();
  for (const [word, colour] of Object.entries(COLOUR_WORDS)) {
    if (text.includes(word)) return colour;
  }
  // Nothing to go on: a stable, muted colour from the name, so the same item
  // always looks the same square to square.
  let hash = 0;
  const name = item?.name || '';
  for (let i = 0; i < name.length; i++) hash = (hash * 31 + name.charCodeAt(i)) % 360;
  return `hsl(${hash}, 14%, 34%)`;
}

/** Readable text over whatever swatch colour we picked. */
function swatchTextFor(item) {
  const c = swatchColorFor(item);
  let lum = 0.45;
  if (c.startsWith('#')) {
    const n = parseInt(c.slice(1), 16);
    lum = (((n >> 16) & 255) * 0.299 + ((n >> 8) & 255) * 0.587 + (n & 255) * 0.114) / 255;
  } else {
    const m = c.match(/,\s*(\d+)%\)$/);
    lum = m ? Number(m[1]) / 100 : 0.4;
  }
  return lum > 0.6 ? '#1a1a1a' : '#f5f2ec';
}

function initialsFor(name) {
  return (name || '?')
    .split(/[\s\-—/]+/)
    .filter(w => /[a-z0-9]/i.test(w))
    .slice(0, 2)
    .map(w => w[0].toUpperCase())
    .join('') || '?';
}

const SCHEDULE_TYPES = [
  { value: 'tile', label: 'Tile' },
  { value: 'paint_wallpaper', label: 'Paint / Wallpaper' },
  { value: 'wood_mixed', label: 'Wood / Mixed' },
];

// Placeable finish categories (placed as objects on a surface via the fixture pipeline)
/* ==================== PALETTE GROUPING ====================
 * The palette used to list every room product in FF&E order, which put sofas
 * and chairs first and buried the finishes. Finishes are what this page is
 * for, so they lead; the rest stay one click away.
 * Groups with no items are not offered at all - a living room simply never
 * shows a PLUMBING chip.
 */
const PALETTE_GROUPS = [
  // "floor" must not swallow a floor LAMP, nor "counter" a counter STOOL.
  { key: 'finish',    label: 'FINISHES',  test: /paint|wallpaper|tile|stone|slab|counter(?!\s*stool)|floor(?!\s*lamp)|trim|moulding|molding|baseboard|grout|backsplash|wainscot|finish|surface|carpet|marble|quartz/i },
  { key: 'plumbing',  label: 'PLUMBING',  test: /plumb|faucet|sink|toilet|tub|shower|drain|valve|bidet|basin|spout/i },
  // 'fixture' is deliberately absent - it means plumbing as often as lighting,
  // so a bare "Fixtures" category must fall through to the sub-category.
  { key: 'lighting',  label: 'LIGHTING',  test: /light|chandelier|pendant|sconce|lamp/i },
  { key: 'furniture', label: 'FURNITURE', test: /furnitur|sofa|chair|table|bed\b|desk|dresser|cabinet|stool|bench|ottoman|nightstand|console|shelv/i },
  { key: 'decor',     label: 'DECOR',     test: /decor|art\b|accessor|mirror|pillow|drapery|window treatment|rug|throw/i },
];
const PALETTE_OTHER = { key: 'other', label: 'OTHER' };

/**
 * Which palette group an item belongs to.
 *
 * Signals are tried strongest-first: the FF&E category, then the sub-category,
 * then the product name. Matching everything at once mislabels things - a
 * "Stone Side Table" is furniture, not a stone finish, and its category says so.
 */
export function classifyPaletteItem(item) {
  for (const field of [item?.categoryName, item?.subcategoryName, item?.name]) {
    if (!field) continue;
    const group = PALETTE_GROUPS.find(g => g.test.test(field));
    if (group) return group.key;
  }
  return PALETTE_OTHER.key;
}

const FIXTURE_CATEGORIES = [
  { key: 'fixture', label: 'Fixture', color: '#2563eb' },
  { key: 'trim', label: 'Trim', color: '#0d9488' },
  { key: 'shower_glass', label: 'Glass', color: '#0284c7' },
  { key: 'mirror', label: 'Mirror', color: '#7c3aed' },
  { key: 'electrical', label: 'Electrical', color: '#d97706' },
  { key: 'art', label: 'Art', color: '#db2777' },
];
const FIXTURE_CATEGORY_COLORS = FIXTURE_CATEGORIES.reduce((m, c) => { m[c.key] = c.color; return m; }, {});
const FIXTURE_CATEGORY_LABELS = FIXTURE_CATEGORIES.reduce((m, c) => { m[c.key] = c.label; return m; }, {});

const TILE_PATTERNS = [
  { value: 'stacked_horizontal', label: 'STACKED HORIZONTAL' },
  { value: 'stacked_vertical', label: 'STACKED VERTICAL' },
  { value: 'offset', label: 'OFFSET (BRICK)' },
  { value: 'one_third_offset', label: '1/3 OFFSET' },
  { value: 'herringbone', label: 'HERRINGBONE' },
  { value: 'herringbone_vertical', label: 'HERRINGBONE VERTICAL' },
  { value: 'basket_weave', label: 'BASKET WEAVE' },
  { value: 'stepladder', label: 'STEPLADDER' },
  { value: 'diagonal', label: 'DIAGONAL' },
];

const GROUT_COLORS = [
  { value: '#FFFFFF', label: 'White' },
  { value: '#D4D4D4', label: 'Light Gray' },
  { value: '#888888', label: 'Gray' },
  { value: '#4a4035', label: 'Brown' },
  { value: '#333333', label: 'Charcoal' },
  { value: '#111111', label: 'Black' },
];


const DEFAULT_ZONE_CONFIG = [
  { id: 'main_wall', label: 'Main Wall', height: 85, dimension: '' },
  { id: 'floor', label: 'Floor', height: 15, dimension: '' },
];

const getItemImage = (item) =>
  item.image_url || item.finish_image || item.image || (item.photos?.length ? item.photos[0] : '') || '';

const ensureSurfaceDefaults = (s) => ({
  ...s,
  zone_config: s.zone_config || DEFAULT_ZONE_CONFIG.map(z => ({ ...z })),
  materials: s.materials || [],
  niches: s.niches || [],
  benches: s.benches || [],
  fixtures: s.fixtures || [],
  patches: s.patches || [],
  surface_type: s.surface_type || 'wall',
});

const ensureAllSurfaces = (surfaces) => {
  let result = (surfaces || []).map(ensureSurfaceDefaults);
  if (!result.find(s => s.surface_type === 'ceiling')) result.push(ensureSurfaceDefaults({ id: crypto.randomUUID(), name: 'Ceiling', surface_type: 'ceiling', materials: [] }));
  if (!result.find(s => s.surface_type === 'floor')) result.push(ensureSurfaceDefaults({ id: crypto.randomUUID(), name: 'Floor', surface_type: 'floor', materials: [] }));
  return result;
};


/* ==================== SVG PATTERN THUMBNAILS ==================== */
const PatternThumb = ({ pattern, size = 60 }) => {
  const s = size, f = '#d6e4ed', k = '#555', w = 0.6;
  switch (pattern) {
    case 'stacked_horizontal':
      return (<svg width={s} height={s} viewBox="0 0 40 40"><rect width="40" height="40" fill="#e8e8e8"/>{[0,10,20,30].map(y => [0,20].map(x => <rect key={`${x}${y}`} x={x+.5} y={y+.5} width={19} height={9} fill={f} stroke={k} strokeWidth={w} rx=".3"/>))}</svg>);
    case 'stacked_vertical':
      return (<svg width={s} height={s} viewBox="0 0 40 40"><rect width="40" height="40" fill="#e8e8e8"/>{[0,10,20,30].map(x => [0,20].map(y => <rect key={`${x}${y}`} x={x+.5} y={y+.5} width={9} height={19} fill={f} stroke={k} strokeWidth={w} rx=".3"/>))}</svg>);
    case 'offset':
      return (<svg width={s} height={s} viewBox="0 0 40 40"><rect width="40" height="40" fill="#e8e8e8"/>{[0,10,20,30].map((y,i) => [-10+(i%2)*10, 10+(i%2)*10, 30+(i%2)*10].map(x => <rect key={`${x}${y}`} x={x+.5} y={y+.5} width={19} height={9} fill={f} stroke={k} strokeWidth={w} rx=".3"/>))}</svg>);
    case 'one_third_offset':
      return (<svg width={s} height={s} viewBox="0 0 42 40"><rect width="42" height="40" fill="#e8e8e8"/>{[0,10,20,30].map((y,i) => {const o=(i%3)*7; return [-14+o,7+o,28+o].map(x=><rect key={`${x}${y}`} x={x+.5} y={y+.5} width={20} height={9} fill={f} stroke={k} strokeWidth={w} rx=".3"/>)})}</svg>);
    case 'herringbone':
      return (<svg width={s} height={s} viewBox="0 0 48 48"><rect width="48" height="48" fill="#e8e8e8"/><g strokeWidth={w} stroke={k}>{[0,24].map(bx=>[0,24].map(by=><g key={`${bx}${by}`} transform={`translate(${bx},${by})`}><rect x="2" y="0" width="5" height="22" fill={f} rx=".3" transform="rotate(45 12 12)"/><rect x="17" y="0" width="5" height="22" fill={f} rx=".3" transform="rotate(-45 12 12)"/></g>))}</g></svg>);
    case 'herringbone_vertical':
      return (<svg width={s} height={s} viewBox="0 0 40 40"><rect width="40" height="40" fill="#e8e8e8"/><rect x="8" y="2" width="5" height="16" fill={f} stroke={k} strokeWidth={w} rx=".3"/><rect x="15" y="10" width="5" height="16" fill={f} stroke={k} strokeWidth={w} rx=".3"/><rect x="22" y="2" width="5" height="16" fill={f} stroke={k} strokeWidth={w} rx=".3"/></svg>);
    case 'basket_weave':
      return (<svg width={s} height={s} viewBox="0 0 40 40"><rect width="40" height="40" fill="#e8e8e8"/>{[0,20].map(y=>[0,20].map(x=><g key={`h${x}${y}`}><rect x={x+1} y={y+1} width={18} height={8} fill={f} stroke={k} strokeWidth={w} rx=".3"/><rect x={x+1} y={y+11} width={18} height={8} fill={f} stroke={k} strokeWidth={w} rx=".3"/></g>))}</svg>);
    case 'stepladder':
      return (<svg width={s} height={s} viewBox="0 0 40 40"><rect width="40" height="40" fill="#e8e8e8"/>{[0,8,16,24,32].map((x,i)=>{const o=(i%4)*5; return [-10+o,10+o,30+o].map(y=><rect key={`${x}${y}`} x={x+.5} y={y+.5} width={7} height={19} fill={f} stroke={k} strokeWidth={w} rx=".3"/>)})}</svg>);
    case 'diagonal':
      return (<svg width={s} height={s} viewBox="0 0 40 40"><rect width="40" height="40" fill="#e8e8e8"/><g transform="translate(20,20) rotate(45)">{[-20,-10,0,10].map(y=>[-30,-10,10].map(x=><rect key={`${x}${y}`} x={x+.5} y={y+.5} width={19} height={9} fill={f} stroke={k} strokeWidth={w} rx=".3"/>))}</g></svg>);
    default:
      return (<svg width={s} height={s} viewBox="0 0 40 40"><rect width="40" height="40" fill="#e8e8e8"/><rect x="1" y="1" width="38" height="38" fill={f} stroke={k} strokeWidth={w}/></svg>);
  }
};


/* ==================== TILE CROP MODAL ==================== */
const TileCropModal = ({ imageUrl, existingCrop, onConfirm, onCancel }) => {
  const canvasRef = useRef(null);
  const previewCanvasRef = useRef(null);
  const imgRef = useRef(null);
  const [imgLoaded, setImgLoaded] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [startPt, setStartPt] = useState(null);
  const [crop, setCrop] = useState(existingCrop || null);
  const scaleRef = useRef(1);

  useEffect(() => {
    const proxyUrl = `${API_URL}/api/proxy-image?url=${encodeURIComponent(imageUrl)}`;
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => { imgRef.current = img; setImgLoaded(true); };
    img.onerror = () => { const img2 = new Image(); img2.onload = () => { imgRef.current = img2; setImgLoaded(true); }; img2.src = imageUrl; };
    img.src = proxyUrl;
  }, [imageUrl]);

  const redraw = useCallback((currentCrop) => {
    const canvas = canvasRef.current; const img = imgRef.current;
    if (!canvas || !img) return;
    const maxW = 520, maxH = 420;
    const s = Math.min(maxW / img.naturalWidth, maxH / img.naturalHeight, 1);
    scaleRef.current = s;
    canvas.width = Math.round(img.naturalWidth * s); canvas.height = Math.round(img.naturalHeight * s);
    const ctx = canvas.getContext('2d');
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
    if (currentCrop && currentCrop.w > 2 && currentCrop.h > 2) {
      ctx.fillStyle = 'rgba(0,0,0,0.55)'; ctx.fillRect(0, 0, canvas.width, canvas.height);
      const cx = currentCrop.x * s, cy = currentCrop.y * s, cw = currentCrop.w * s, ch = currentCrop.h * s;
      ctx.drawImage(img, currentCrop.x, currentCrop.y, currentCrop.w, currentCrop.h, cx, cy, cw, ch);
      ctx.strokeStyle = '#00ff88'; ctx.lineWidth = 2; ctx.setLineDash([6, 3]); ctx.strokeRect(cx, cy, cw, ch); ctx.setLineDash([]);
      const preview = previewCanvasRef.current;
      if (preview) {
        const pSize = 120; const aspect = currentCrop.w / currentCrop.h;
        preview.width = aspect >= 1 ? pSize : Math.round(pSize * aspect);
        preview.height = aspect >= 1 ? Math.round(pSize / aspect) : pSize;
        preview.getContext('2d').drawImage(img, currentCrop.x, currentCrop.y, currentCrop.w, currentCrop.h, 0, 0, preview.width, preview.height);
      }
    }
  }, []);

  useEffect(() => { if (imgLoaded) redraw(crop); }, [imgLoaded, crop, redraw]);

  const getImageCoords = (e) => {
    const rect = canvasRef.current.getBoundingClientRect(); const s = scaleRef.current;
    return { x: (e.clientX - rect.left) / s, y: (e.clientY - rect.top) / s };
  };

  const validCrop = crop && crop.w > 10 && crop.h > 10;

  const handleUseFullImage = () => {
    const img = imgRef.current;
    if (!img) return;
    // Auto-extract tile: use 80% of the image (trim edges only)
    const cropW = Math.round(img.naturalWidth * 0.8);
    const cropH = Math.round(img.naturalHeight * 0.8);
    const cropX = Math.round((img.naturalWidth - cropW) / 2);
    const cropY = Math.round((img.naturalHeight - cropH) / 2);
    onConfirm({ x: cropX, y: cropY, w: cropW, h: cropH });
  };

  return (
    <div className="fixed inset-0 z-[999] flex items-center justify-center" style={{ background: 'rgba(0,0,0,0.88)' }} data-testid="tile-crop-modal">
      <div className="rounded-xl p-6 max-w-[720px] w-full mx-4" style={{ background: '#1c1c2a', border: '1px solid rgba(255,255,255,0.1)' }}>
        <h3 className="text-white font-black text-lg mb-1">EXTRACT SINGLE TILE</h3>
        <p className="text-white/50 text-sm mb-4">Draw a rectangle around <span className="text-green-400 font-bold">ONE individual tile</span>, or use the full image.</p>
        <div className="flex gap-5 items-start">
          <div className="flex-1 overflow-auto rounded border border-white/10" style={{ background: '#111' }}>
            {!imgLoaded ? <div className="flex items-center justify-center h-48 text-white/30 text-sm">Loading...</div> : (
              <canvas ref={canvasRef} className="cursor-crosshair block mx-auto"
                onMouseDown={e => { e.preventDefault(); setDragging(true); setStartPt(getImageCoords(e)); setCrop(null); }}
                onMouseMove={e => { if (!dragging || !startPt) return; const pt = getImageCoords(e); setCrop({ x: Math.round(Math.min(startPt.x, pt.x)), y: Math.round(Math.min(startPt.y, pt.y)), w: Math.round(Math.abs(pt.x - startPt.x)), h: Math.round(Math.abs(pt.y - startPt.y)) }); }}
                onMouseUp={() => setDragging(false)} onMouseLeave={() => { if (dragging) setDragging(false); }} />
            )}
          </div>
          {validCrop && (
            <div className="w-40 flex-shrink-0">
              <div className="text-[10px] font-black text-green-400 mb-2 tracking-wider">EXTRACTED TILE:</div>
              <div className="rounded border-2 border-green-400/50 overflow-hidden" style={{ background: '#fff' }}><canvas ref={previewCanvasRef} className="block mx-auto" /></div>
              <div className="text-[9px] text-white/30 mt-1.5">{crop.w} x {crop.h} px</div>
            </div>
          )}
        </div>
        <div className="flex gap-3 mt-5 flex-wrap">
          <button disabled={!validCrop} onClick={() => onConfirm(crop)} className="px-6 py-2.5 rounded-lg text-sm font-black text-black bg-green-400 hover:bg-green-300 disabled:opacity-30 disabled:cursor-not-allowed" data-testid="crop-confirm-btn">USE THIS TILE</button>
          <button onClick={handleUseFullImage} disabled={!imgLoaded}
            className="px-6 py-2.5 rounded-lg text-sm font-black text-black bg-amber-400 hover:bg-amber-300 disabled:opacity-30 disabled:cursor-not-allowed" data-testid="use-full-image-btn">
            USE FULL IMAGE
          </button>
          <button onClick={onCancel} className="px-4 py-2.5 rounded-lg text-sm text-white/50 border border-white/20 hover:border-white/40" data-testid="crop-cancel-btn">Cancel</button>
        </div>
      </div>
    </div>
  );
};


/* ==================== CANVAS TILE PATTERN RENDERER ==================== */
const drawTilePattern = (ctx, tileImg, pattern, w, h, groutColor, orientation, tileScale, offsetY = 0, hasCrop = false) => {
  const g = 0; // No gap — tiles placed seamlessly side by side
  ctx.fillStyle = groutColor || '#4a4035';
  ctx.fillRect(0, 0, w, h);
  const imgW = tileImg.width || tileImg.naturalWidth || 200;
  const imgH = tileImg.height || tileImg.naturalHeight || 200;
  const scale = tileScale || 1.0;

  const drawTile = (x, y, tw, th) => {
    if (x + tw < 0 || x > w || y + th < 0 || y > h) return;
    ctx.drawImage(tileImg, 0, 0, imgW, imgH, x, y, tw, th);
  };

  // FIXED base 90px — same on ALL walls (back, side, floor, ceiling) for consistency
  const basePx = 90;
  const longSide = Math.max(imgW, imgH);
  const shortSide = Math.min(imgW, imgH);
  const ratio = Math.max(1, longSide / shortSide);
  let tl = Math.max(12, Math.round(basePx * scale));
  let ts = Math.max(6, Math.round(tl / ratio));
  if (orientation === 'vertical') { const tmp = tl; tl = ts; ts = tmp; }

  const stepH = ts + g || ts;
  const stepV = tl + g || tl;
  const oY = offsetY > 0 ? (offsetY % stepH) : 0;
  const baseRow = offsetY > 0 ? Math.floor(offsetY / stepH) : 0;

  switch (pattern) {
    case 'stacked_horizontal':
      for (let y = -ts - oY; y < h + ts; y += stepH) for (let x = -tl; x < w + tl; x += tl + g) drawTile(x, y, tl, ts);
      break;
    case 'stacked_vertical': {
      const oYv = offsetY > 0 ? (offsetY % stepV) : 0;
      for (let x = -ts; x < w + ts; x += ts + g) for (let y = -tl - oYv; y < h + tl; y += stepV) drawTile(x, y, ts, tl);
      break;
    }
    case 'offset': {
      let row = 0;
      for (let y = -ts - oY; y < h + ts; y += stepH, row++) {
        const actualRow = baseRow + row;
        const off = (actualRow % 2) * ((tl + g) / 2);
        for (let x = -tl * 2; x < w + tl * 2; x += tl + g) drawTile(x + off, y, tl, ts);
      }
      break;
    }
    case 'one_third_offset': {
      let row = 0;
      for (let y = -ts - oY; y < h + ts; y += stepH, row++) {
        const actualRow = baseRow + row;
        const off = (actualRow % 3) * ((tl + g) / 3);
        for (let x = -tl * 2; x < w + tl * 2; x += tl + g) drawTile(x + off, y, tl, ts);
      }
      break;
    }
    case 'herringbone': {
      const tw = Math.max(15, ts); const th = Math.max(35, tl);
      for (let row = -4; row < Math.ceil(h / (tw + g || tw)) + 4; row++) {
        for (let col = -4; col < Math.ceil(w / (th + g || th)) + 4; col++) {
          const bx = col * (th + g); const by = row * (tw * 2 + g * 2) - oY;
          drawTile(bx, by + (col % 2) * (tw + g), th, tw);
          drawTile(bx + th - tw, by + (col % 2) * (tw + g) + tw + g, tw, th);
        }
      }
      break;
    }
    case 'herringbone_vertical': {
      const tw = Math.max(12, ts); const th = Math.max(35, tl);
      const oYhv = offsetY > 0 ? (offsetY % (th / 2 + g || th / 2)) : 0;
      for (let col = -4; col < Math.ceil(w / (tw + g || tw)) + 4; col++) {
        for (let row = -4; row < Math.ceil(h / (th / 2 + g || th / 2)) + 4; row++) {
          const x = col * (tw * 2 + g); const y = row * (th / 2 + g) - oYhv;
          if (row % 2 === 0) { drawTile(x, y, tw, th); drawTile(x + tw + g, y + th / 2, tw, th); }
          else { drawTile(x + tw + g, y, tw, th); drawTile(x, y + th / 2, tw, th); }
        }
      }
      break;
    }
    case 'basket_weave': {
      const block = ts * 2 + g; const cell = block + g || block;
      const oYbw = offsetY > 0 ? (offsetY % cell) : 0;
      for (let gy = -2; gy < Math.ceil(h / cell) + 2; gy++) for (let gx = -2; gx < Math.ceil(w / cell) + 2; gx++) {
        const bx = gx * cell, by = gy * cell - oYbw;
        if ((gx + gy) % 2 === 0) { drawTile(bx, by, block, ts); drawTile(bx, by + ts + g, block, ts); }
        else { drawTile(bx, by, ts, block); drawTile(bx + ts + g, by, ts, block); }
      }
      break;
    }
    case 'stepladder': {
      const oYsl = offsetY > 0 ? (offsetY % stepV) : 0;
      let col = 0;
      for (let x = -ts; x < w + ts; x += ts + g, col++) { const off = ((col % 4) * ((tl + g) / 4)); for (let y = -tl * 2 + off - oYsl; y < h + tl; y += stepV) drawTile(x, y, ts, tl); }
      break;
    }
    case 'diagonal': {
      const sq = Math.max(15, Math.round(Math.sqrt(tl * ts))); const step = sq + g || sq;
      ctx.save(); ctx.translate(w / 2, h / 2); ctx.rotate(Math.PI / 4);
      const range = Math.max(w, h) * 1.5;
      for (let y = -range; y < range; y += step) for (let x = -range; x < range; x += step) drawTile(x, y, sq, sq);
      ctx.restore(); break;
    }
    default: ctx.drawImage(tileImg, 0, 0, w, h);
  }
};


/* ==================== TILE PATTERN CANVAS ==================== */
const TilePatternCanvas = ({ pattern, imageUrl, tileCrop, groutColor, tileOrientation, tileScale, zoneTopPct, zoneHeightPct }) => {
  const containerRef = React.useRef(null);
  const canvasRef = React.useRef(null);
  const imgRef = React.useRef(null);

  const draw = React.useCallback(() => {
    const container = containerRef.current; const canvas = canvasRef.current; const img = imgRef.current;
    if (!container || !canvas || !img || !img.complete || img.naturalWidth === 0) return;
    const rect = container.getBoundingClientRect();
    const w = Math.round(rect.width); const h = Math.round(rect.height);
    if (w < 10 || h < 10) return;
    canvas.width = w; canvas.height = h;
    canvas.style.width = w + 'px'; canvas.style.height = h + 'px';
    const ctx = canvas.getContext('2d');
    // Use crop if available, otherwise use 80% of image (trim edges)
    const crop = (tileCrop && tileCrop.w > 0 && tileCrop.h > 0) ? tileCrop : {
      x: Math.round(img.naturalWidth * 0.1),
      y: Math.round(img.naturalHeight * 0.1),
      w: Math.round(img.naturalWidth * 0.8),
      h: Math.round(img.naturalHeight * 0.8),
    };
    const tileCanvas = document.createElement('canvas');
    tileCanvas.width = Math.max(1, Math.round(crop.w)); tileCanvas.height = Math.max(1, Math.round(crop.h));
    tileCanvas.getContext('2d').drawImage(img, crop.x, crop.y, crop.w, crop.h, 0, 0, tileCanvas.width, tileCanvas.height);
    let offsetY = 0;
    if (zoneTopPct > 0 && zoneHeightPct > 0) {
      const wallPxH = h * (100 / zoneHeightPct);
      offsetY = wallPxH * (zoneTopPct / 100);
    }
    drawTilePattern(ctx, tileCanvas, pattern, w, h, groutColor, tileOrientation, tileScale, offsetY, true);
  }, [pattern, tileCrop, groutColor, tileOrientation, tileScale, zoneTopPct, zoneHeightPct]);

  React.useEffect(() => {
    if (!imageUrl) return;
    const proxyUrl = `${API_URL}/api/proxy-image?url=${encodeURIComponent(imageUrl)}`;
    const img = new Image(); img.crossOrigin = 'anonymous';
    img.onload = () => { imgRef.current = img; draw(); };
    img.onerror = () => { const img2 = new Image(); img2.onload = () => { imgRef.current = img2; draw(); }; img2.src = imageUrl; };
    img.src = proxyUrl;
    return () => { img.onload = null; img.onerror = null; };
  }, [imageUrl, draw]);

  React.useEffect(() => { draw(); }, [draw]);
  React.useEffect(() => {
    const container = containerRef.current; if (!container) return;
    const ro = new ResizeObserver(() => draw()); ro.observe(container);
    return () => ro.disconnect();
  }, [draw]);

  return (
    <div ref={containerRef} className="absolute inset-0 overflow-hidden">
      <canvas ref={canvasRef} style={{ position: 'absolute', top: 0, left: 0, display: 'block' }} />
    </div>
  );
};


/* ==================== NICHE / BENCH / FIXTURE OVERLAYS ==================== */
const NicheOverlay = ({ niche, isSelected, onClick, onDragStart, selectedItem, onApplyTile }) => (
  <div
    data-testid={`niche-${niche.id}`}
    className={`absolute cursor-grab active:cursor-grabbing transition-shadow ${isSelected ? 'ring-2 ring-cyan-400 z-20' : 'z-10 hover:ring-1 hover:ring-cyan-300/50'}`}
    style={{
      left: `${niche.x}%`, top: `${niche.y}%`, width: `${niche.w}%`, height: `${niche.h}%`,
      boxShadow: 'inset 0 2px 10px rgba(0,0,0,0.5), inset 0 -1px 4px rgba(0,0,0,0.2)',
      border: niche.trim === 'schluter' ? '3px solid #c0c0c0' : niche.trim === 'bullnose' ? '3px solid #e8e0d4' : niche.trim === 'pencil' ? '2px solid #8b7355' : '1px solid rgba(0,0,0,0.4)',
      background: '#c8c4bc', borderRadius: '2px',
    }}
    onMouseDown={e => {
      e.stopPropagation(); e.preventDefault();
      if (selectedItem) { onApplyTile(); }
      else { onDragStart(e, 'niche', niche); }
    }}>
    {niche.material?.image && niche.material?.pattern && (
      <TilePatternCanvas pattern={niche.material.pattern} imageUrl={niche.material.image}
        tileCrop={niche.material.tile_crop} groutColor={niche.material.grout_color}
        tileOrientation={niche.material.tile_orientation} tileScale={niche.material.tile_scale} />
    )}
    <div className="absolute bottom-0 left-0 right-0 px-1 py-0.5 text-[7px] font-black text-white/80 bg-black/60 text-center pointer-events-none">NICHE</div>
    {isSelected && <>
      <div className="absolute -top-1 -left-1 w-2.5 h-2.5 bg-cyan-400 border border-white rounded-full cursor-nw-resize z-30"
        onMouseDown={e => { e.stopPropagation(); e.preventDefault(); onDragStart(e, 'niche-resize-tl', niche); }} />
      <div className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-cyan-400 border border-white rounded-full cursor-ne-resize z-30"
        onMouseDown={e => { e.stopPropagation(); e.preventDefault(); onDragStart(e, 'niche-resize-tr', niche); }} />
      <div className="absolute -bottom-1 -left-1 w-2.5 h-2.5 bg-cyan-400 border border-white rounded-full cursor-sw-resize z-30"
        onMouseDown={e => { e.stopPropagation(); e.preventDefault(); onDragStart(e, 'niche-resize-bl', niche); }} />
      <div className="absolute -bottom-1 -right-1 w-2.5 h-2.5 bg-cyan-400 border border-white rounded-full cursor-se-resize z-30"
        onMouseDown={e => { e.stopPropagation(); e.preventDefault(); onDragStart(e, 'niche-resize-br', niche); }} />
    </>}
  </div>
);

const BenchOverlay = ({ bench, isSelected, onClick, onDragStart, selectedItem, onApplyTile }) => (
  <div
    data-testid={`bench-${bench.id}`}
    className={`absolute cursor-grab active:cursor-grabbing transition-shadow ${isSelected ? 'ring-2 ring-orange-400 z-20' : 'z-10 hover:ring-1 hover:ring-orange-300/50'}`}
    style={{
      left: `${bench.x}%`, top: `${bench.y}%`, width: `${bench.w}%`, height: `${bench.h}%`,
      boxShadow: '0 3px 8px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.2)',
      border: bench.trim === 'schluter' ? '3px solid #c0c0c0' : bench.trim === 'bullnose' ? '3px solid #e8e0d4' : bench.trim === 'pencil' ? '2px solid #8b7355' : '1px solid rgba(0,0,0,0.3)',
      background: '#ddd8d0', borderRadius: '2px',
    }}
    onMouseDown={e => {
      e.stopPropagation(); e.preventDefault();
      if (selectedItem) { onApplyTile(); }
      else { onDragStart(e, 'bench', bench); }
    }}>
    {bench.material?.image && bench.material?.pattern && (
      <TilePatternCanvas pattern={bench.material.pattern} imageUrl={bench.material.image}
        tileCrop={bench.material.tile_crop} groutColor={bench.material.grout_color}
        tileOrientation={bench.material.tile_orientation} tileScale={bench.material.tile_scale} />
    )}
    <div className="absolute top-0 left-0 right-0 h-[6px] bg-gradient-to-b from-white/20 to-transparent pointer-events-none" />
    <div className="absolute bottom-0 left-0 right-0 px-1 py-0.5 text-[7px] font-black text-white/80 bg-black/60 text-center pointer-events-none">BENCH</div>
    {isSelected && <>
      <div className="absolute -top-1 -left-1 w-2.5 h-2.5 bg-orange-400 border border-white rounded-full cursor-nw-resize z-30"
        onMouseDown={e => { e.stopPropagation(); e.preventDefault(); onDragStart(e, 'bench-resize-tl', bench); }} />
      <div className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-orange-400 border border-white rounded-full cursor-ne-resize z-30"
        onMouseDown={e => { e.stopPropagation(); e.preventDefault(); onDragStart(e, 'bench-resize-tr', bench); }} />
      <div className="absolute -bottom-1 -left-1 w-2.5 h-2.5 bg-orange-400 border border-white rounded-full cursor-sw-resize z-30"
        onMouseDown={e => { e.stopPropagation(); e.preventDefault(); onDragStart(e, 'bench-resize-bl', bench); }} />
      <div className="absolute -bottom-1 -right-1 w-2.5 h-2.5 bg-orange-400 border border-white rounded-full cursor-se-resize z-30"
        onMouseDown={e => { e.stopPropagation(); e.preventDefault(); onDragStart(e, 'bench-resize-br', bench); }} />
    </>}
  </div>
);

const FixtureOverlay = ({ fixture, isSelected, onClick, onDragStart }) => (
  <div
    data-testid={`fixture-${fixture.id}`}
    className={`absolute cursor-grab active:cursor-grabbing z-10 ${isSelected ? 'ring-2 ring-blue-400 scale-110' : 'hover:scale-105'}`}
    style={{
      left: `${fixture.x}%`, top: `${fixture.y}%`, transform: 'translate(-50%, -50%)',
      width: '36px', height: '36px', borderRadius: '50%',
      background: 'rgba(20,20,30,0.85)', border: '2px solid rgba(100,160,255,0.5)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'transform 0.15s',
    }}
    onMouseDown={e => { e.stopPropagation(); e.preventDefault(); onDragStart(e, 'fixture', fixture); }}>
    {fixture.image ? <img src={fixture.image} alt="" className="w-5 h-5 rounded-full object-cover pointer-events-none" /> : <Droplet size={14} className="text-blue-400 pointer-events-none" />}
    <div className="absolute -bottom-3.5 left-1/2 -translate-x-1/2 px-1 py-0 rounded text-[6px] font-black text-white whitespace-nowrap pointer-events-none" style={{ background: FIXTURE_CATEGORY_COLORS[fixture.category] || 'rgba(0,0,0,0.8)' }}>{fixture.category && fixture.category !== 'fixture' ? `${FIXTURE_CATEGORY_LABELS[fixture.category]}: ` : ''}{fixture.name || 'Fixture'}</div>
  </div>
);


/* ==================== WALL ZONE ==================== */
const WallZone = ({ zone, zoneHeight, mat, compact, niches, benches, fixtures,
  surfaceId, onClickZone, onRemove, onOpenPattern, onCropTile, selectedElement, onDragStart,
  selectedItem, onApplyTileToElement }) => {
  const zoneRef = useRef(null);
  const hasImage = mat?.image;
  const hasCrop = mat?.tile_crop && mat.tile_crop.w > 0;
  const patternLabel = mat?.pattern ? TILE_PATTERNS.find(p => p.value === mat.pattern)?.label : null;

  const wrappedDragStart = useCallback((e, type, el) => {
    const rect = zoneRef.current?.getBoundingClientRect();
    if (rect && surfaceId) onDragStart(e, type, el, surfaceId, rect);
  }, [surfaceId, onDragStart]);

  const wrappedApplyTile = useCallback((elementType, elementId) => {
    if (surfaceId && selectedItem) onApplyTileToElement(surfaceId, elementType, elementId, selectedItem);
  }, [surfaceId, selectedItem, onApplyTileToElement]);

  return (
    <div
      ref={zoneRef}
      className="absolute left-0 right-0 cursor-pointer transition-all group overflow-hidden"
      style={{ top: `${zone.top}%`, height: `${zoneHeight}%` }}
      onClick={onClickZone}
      data-testid={`wall-zone-${zone.id}`}
    >
      {hasImage ? (
        <TilePatternCanvas key={`${mat.pattern || 'sh'}-${mat.id}-${hasCrop ? 'c' : 'r'}-${mat.grout_color}-${mat.tile_orientation}-${mat.tile_scale}-${zone.top}`}
          pattern={mat.pattern || 'stacked_horizontal'} imageUrl={mat.image} tileCrop={mat.tile_crop}
          groutColor={mat.grout_color} tileOrientation={mat.tile_orientation} tileScale={mat.tile_scale}
          zoneTopPct={zone.top} zoneHeightPct={zoneHeight} />
      ) : (
        <div className="absolute inset-0" style={{ background: '#4a4842', borderBottom: '1px solid #333' }}>
          <div className="absolute inset-0 flex items-center justify-center"><span className="text-white/20 text-[8px] font-bold">CLICK TO PLACE</span></div>
        </div>
      )}
      {/* Niches */}
      {(niches || []).map(n => <NicheOverlay key={n.id} niche={n}
        isSelected={selectedElement?.id === n.id} onDragStart={wrappedDragStart}
        selectedItem={selectedItem} onApplyTile={() => wrappedApplyTile('niche', n.id)} />)}
      {/* Benches */}
      {(benches || []).map(b => <BenchOverlay key={b.id} bench={b}
        isSelected={selectedElement?.id === b.id} onDragStart={wrappedDragStart}
        selectedItem={selectedItem} onApplyTile={() => wrappedApplyTile('bench', b.id)} />)}
      {/* Fixtures */}
      {(fixtures || []).map(f => <FixtureOverlay key={f.id} fixture={f}
        isSelected={selectedElement?.id === f.id} onDragStart={wrappedDragStart} />)}
      {/* Zone info bar — hidden when tile applied, shows on hover */}
      <div className={`absolute bottom-0 left-0 right-0 flex items-center justify-between px-1.5 ${compact ? 'py-0' : 'py-0.5'} ${hasImage ? 'opacity-0 group-hover:opacity-100' : ''} transition-opacity`}
        style={hasImage ? { background: 'rgba(0,0,0,0.65)', backdropFilter: 'blur(4px)' } : { borderTop: '1px solid rgba(255,255,255,0.1)' }}>
        <div className="flex items-center gap-1 flex-wrap min-w-0">
          <span className={`text-[${compact ? '7' : '9'}px] font-black uppercase tracking-wider ${hasImage ? 'text-white' : 'text-gray-400'}`}>{zone.label}</span>
          {zone.dimension && <span className="text-[8px] font-mono text-amber-300/80 bg-black/40 px-1 rounded">{zone.dimension}</span>}
          {patternLabel && !compact && <span className="px-1 rounded text-[7px] font-black text-amber-300 bg-black/60">{patternLabel}</span>}
        </div>
        <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
          {mat && <>
            <button onClick={e => { e.stopPropagation(); onCropTile(); }} className={`px-1 py-0.5 rounded text-[8px] font-black flex items-center gap-0.5 ${hasCrop ? 'text-green-300 bg-black/80' : 'text-orange-300 bg-black/80 animate-pulse'}`} data-testid={`crop-btn-${zone.id}`}><Crop size={8} />CROP</button>
            <button onClick={e => { e.stopPropagation(); onOpenPattern(); }} className="px-1 py-0.5 rounded text-[8px] font-black text-amber-300 bg-black/80" data-testid={`pattern-btn-${zone.id}`}>PATTERN</button>
            <button onClick={e => { e.stopPropagation(); onRemove(); }} className="px-0.5 py-0.5 rounded text-[8px] text-red-300 bg-black/80"><Trash2 size={8} /></button>
          </>}
        </div>
      </div>
    </div>
  );
};


/* ==================== 3D SHOWER PERSPECTIVE VIEW ==================== */
const Shower3DView = ({ schedule, selectedItem, placementMode, selectedElement,
  onPlaceItem, onPlaceFixture, onAddNiche, onAddBench, onRemoveMaterial,
  onChangePattern, onCropTile, onChangeGrout, onChangeOrientation, onChangeTileScale,
  onSelectElement, onDeleteElement, onUpdateElement, onApplyTileToElement,
  onResizeZone, onEditDimension, onPlaceCeilingFloor, onAddZone, onRemoveZone }) => {

  const [patternPicker, setPatternPicker] = useState(null);
  const [editingDim, setEditingDim] = useState(null);
  const [dimValue, setDimValue] = useState('');
  const dragRef = useRef(null);
  const callbacksRef = useRef({});
  callbacksRef.current = { onUpdateElement, onResizeZone, onSelectElement };

  // Global drag handlers for element move/resize and zone resize
  useEffect(() => {
    const handleMove = (e) => {
      if (!dragRef.current) return;
      const d = dragRef.current;
      const dx = e.clientX - d.startMouseX;
      const dy = e.clientY - d.startMouseY;
      if (!d.moved && Math.abs(dx) + Math.abs(dy) < 4) return;
      d.moved = true;

      if (d.dragType === 'zone-resize') {
        const deltaPct = (dy / d.wallHeight) * 100;
        const newConfig = d.startConfig.map(z => ({ ...z }));
        const total = newConfig[d.zoneIdx].height + newConfig[d.zoneIdx + 1].height;
        const newH = Math.max(5, Math.min(total - 5, d.startConfig[d.zoneIdx].height + deltaPct));
        newConfig[d.zoneIdx] = { ...newConfig[d.zoneIdx], height: newH };
        newConfig[d.zoneIdx + 1] = { ...newConfig[d.zoneIdx + 1], height: total - newH };
        callbacksRef.current.onResizeZone(d.surfaceId, newConfig);
      } else if (d.dragType.startsWith('niche') || d.dragType.startsWith('bench') || d.dragType === 'fixture') {
        const rect = d.zoneRect;
        if (d.dragType === 'niche' || d.dragType === 'fixture') {
          const newX = Math.max(0, Math.min(95, ((e.clientX - rect.left - d.offsetX) / rect.width) * 100));
          const newY = Math.max(0, Math.min(95, ((e.clientY - rect.top - d.offsetY) / rect.height) * 100));
          const elType = d.dragType === 'fixture' ? 'fixture' : d.dragType;
          callbacksRef.current.onUpdateElement(d.surfaceId, elType, d.elementId, { x: Math.round(newX), y: Math.round(newY) });
        } else if (d.dragType === 'bench') {
          // Bench only moves horizontally — stays at bottom
          const newX = Math.max(0, Math.min(95, ((e.clientX - rect.left - d.offsetX) / rect.width) * 100));
          callbacksRef.current.onUpdateElement(d.surfaceId, 'bench', d.elementId, { x: Math.round(newX) });
        } else if (d.dragType.includes('resize')) {
          // Resize handles (tl, tr, bl, br)
          const corner = d.dragType.split('-').pop(); // tl, tr, bl, br
          const elType = d.dragType.startsWith('niche') ? 'niche' : 'bench';
          const pxToW = (1 / rect.width) * 100;
          const pxToH = (1 / rect.height) * 100;
          let { x, y, w, h } = d.startProps;
          if (elType === 'bench') {
            // Bench resize: only adjust width and height (bottom stays anchored)
            if (corner === 'tl') { x = Math.max(0, d.startProps.x + dx * pxToW); w = Math.max(10, d.startProps.w - dx * pxToW); h = Math.max(5, d.startProps.h - dy * pxToH); }
            else if (corner === 'tr') { w = Math.max(10, d.startProps.w + dx * pxToW); h = Math.max(5, d.startProps.h - dy * pxToH); }
            else if (corner === 'bl') { x = Math.max(0, d.startProps.x + dx * pxToW); w = Math.max(10, d.startProps.w - dx * pxToW); }
            else if (corner === 'br') { w = Math.max(10, d.startProps.w + dx * pxToW); }
          } else {
            if (corner === 'br') { w = Math.max(10, d.startProps.w + dx * pxToW); h = Math.max(10, d.startProps.h + dy * pxToH); }
            else if (corner === 'bl') { x = Math.max(0, d.startProps.x + dx * pxToW); w = Math.max(10, d.startProps.w - dx * pxToW); h = Math.max(10, d.startProps.h + dy * pxToH); }
            else if (corner === 'tr') { y = d.startProps.y; w = Math.max(10, d.startProps.w + dx * pxToW); y = Math.max(0, d.startProps.y + dy * pxToH); h = Math.max(10, d.startProps.h - dy * pxToH); }
            else if (corner === 'tl') { x = Math.max(0, d.startProps.x + dx * pxToW); y = Math.max(0, d.startProps.y + dy * pxToH); w = Math.max(10, d.startProps.w - dx * pxToW); h = Math.max(10, d.startProps.h - dy * pxToH); }
          }
          callbacksRef.current.onUpdateElement(d.surfaceId, elType, d.elementId, { x: Math.round(x), y: Math.round(y), w: Math.round(w), h: Math.round(h) });
        }
      }
    };

    const handleUp = () => {
      if (dragRef.current && !dragRef.current.moved) {
        // Was a click, not a drag — select the element
        const d = dragRef.current;
        if (d.elementId) callbacksRef.current.onSelectElement({ type: d.elementBaseType, id: d.elementId });
      }
      dragRef.current = null;
    };

    document.addEventListener('mousemove', handleMove);
    document.addEventListener('mouseup', handleUp);
    return () => { document.removeEventListener('mousemove', handleMove); document.removeEventListener('mouseup', handleUp); };
  }, []);

  const surfaces = schedule.surfaces || [];
  const walls = surfaces.filter(s => s.surface_type === 'wall');
  const ceiling = surfaces.find(s => s.surface_type === 'ceiling');
  const floor = surfaces.find(s => s.surface_type === 'floor');

  const backWall = walls.find(s => s.name?.toLowerCase().includes('back')) || walls[0];
  const leftWall = walls.find(s => s.name?.toLowerCase().includes('left')) || walls[1];
  const rightWall = walls.find(s => s.name?.toLowerCase().includes('right')) || walls[2];

  const getZoneMap = (surface) => {
    const map = {};
    (surface?.materials || []).forEach(mat => { if (mat.position_label) map[mat.position_label] = mat; });
    return map;
  };

  const getZoneConfig = (surface) => surface?.zone_config || DEFAULT_ZONE_CONFIG.map(z => ({ ...z }));

  const handleZoneClick = (surface, zoneId, e) => {
    if (!surface) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const xPct = ((e.clientX - rect.left) / rect.width) * 100;
    const yPct = ((e.clientY - rect.top) / rect.height) * 100;

    if (placementMode === 'niche') onAddNiche(surface.id, zoneId, xPct, yPct);
    else if (placementMode === 'bench') onAddBench(surface.id, zoneId, xPct, yPct);
    else if (placementMode === 'fixture' && selectedItem) onPlaceFixture(surface.id, zoneId, selectedItem, xPct, yPct);
    else if (selectedItem) onPlaceItem(surface.id, zoneId, selectedItem);
  };

  const handleCeilingFloorClick = (surface) => {
    if (!surface || !selectedItem) return;
    if (placementMode === 'fixture') onPlaceFixture(surface.id, '_surface', selectedItem, 50, 50);
    else onPlaceCeilingFloor(surface.id, selectedItem);
  };

  // Element drag start handler — passed to all overlays
  const handleElementDragStart = (e, dragType, element, providedSurfaceId, providedRect) => {
    const zoneRect = providedRect || (e.target.closest('[data-testid^="wall-zone-"]') || e.target.closest('[data-testid^="surface-"]'))?.getBoundingClientRect();
    if (!zoneRect) return;
    let surfaceId = providedSurfaceId || null;
    let elementBaseType = dragType.replace(/-resize-.+$/, '');
    if (!surfaceId) {
      for (const s of surfaces) {
        if (elementBaseType === 'niche' && (s.niches || []).find(n => n.id === element.id)) { surfaceId = s.id; break; }
        if (elementBaseType === 'bench' && (s.benches || []).find(b => b.id === element.id)) { surfaceId = s.id; break; }
        if (elementBaseType === 'fixture' && (s.fixtures || []).find(f => f.id === element.id)) { surfaceId = s.id; break; }
      }
    }
    if (!surfaceId) return;

    dragRef.current = {
      dragType, surfaceId, elementId: element.id, elementBaseType,
      zoneRect, startMouseX: e.clientX, startMouseY: e.clientY, moved: false,
      offsetX: e.clientX - zoneRect.left - (element.x / 100) * zoneRect.width,
      offsetY: e.clientY - zoneRect.top - (element.y / 100) * zoneRect.height,
      startProps: { x: element.x, y: element.y, w: element.w, h: element.h },
    };
  };

  // Zone resize start handler
  const handleZoneResizeStart = (e, surface, zoneIdx) => {
    e.preventDefault(); e.stopPropagation();
    const wallEl = e.currentTarget.parentElement;
    if (!wallEl) return;
    const wallRect = wallEl.getBoundingClientRect();
    const zones = getZoneConfig(surface);
    dragRef.current = {
      dragType: 'zone-resize', surfaceId: surface.id, zoneIdx,
      startMouseX: e.clientX, startMouseY: e.clientY, moved: false,
      wallHeight: wallRect.height, startConfig: zones.map(z => ({ ...z })),
    };
  };

  const renderWallFace = (surface, compact) => {
    if (!surface) return <div className="absolute inset-0" style={{ background: '#f0ede8' }} />;
    const zoneMap = getZoneMap(surface);
    const zones = getZoneConfig(surface);
    let accTop = 0;
    const elements = [];
    zones.forEach((zone, idx) => {
      const top = accTop;
      accTop += zone.height;
      const mat = zoneMap[zone.id];
      const zoneNiches = (surface.niches || []).filter(n => n.zone_id === zone.id);
      const zoneFixtures = (surface.fixtures || []).filter(f => f.zone_id === zone.id);
      // Benches are rendered at wall level (below), NOT inside zones
      elements.push(
        <WallZone key={zone.id} zone={{ ...zone, top }} zoneHeight={zone.height} mat={mat}
          compact={compact} surfaceId={surface.id}
          niches={zoneNiches} benches={[]} fixtures={zoneFixtures}
          selectedElement={selectedElement}
          selectedItem={selectedItem}
          onDragStart={handleElementDragStart}
          onApplyTileToElement={onApplyTileToElement}
          onClickZone={(e) => handleZoneClick(surface, zone.id, e)}
          onRemove={() => mat && onRemoveMaterial(surface.id, mat.id)}
          onOpenPattern={() => setPatternPicker(patternPicker?.zoneId === zone.id && patternPicker?.surfaceId === surface.id ? null : { surfaceId: surface.id, zoneId: zone.id })}
          onCropTile={() => mat && onCropTile(surface.id, mat.id, mat.image, mat.tile_crop)}
        />
      );
      // Zone resize handle between zones (only on non-compact walls)
      if (!compact && idx < zones.length - 1) {
        elements.push(
          <div key={`resize-${idx}`}
            className="absolute left-0 right-0 z-30 cursor-row-resize group/rz"
            style={{ top: `${accTop}%`, height: '10px', transform: 'translateY(-5px)' }}
            onMouseDown={(e) => handleZoneResizeStart(e, surface, idx)}
            data-testid={`zone-handle-${idx}`}>
            <div className="absolute left-[10%] right-[10%] top-1/2 -translate-y-1/2 h-[3px] rounded bg-transparent group-hover/rz:bg-amber-400/70 transition-all" />
            <div className="absolute left-1/2 -translate-x-1/2 top-1/2 -translate-y-1/2 w-6 h-3 rounded bg-transparent group-hover/rz:bg-amber-400 flex items-center justify-center opacity-0 group-hover/rz:opacity-100 transition-all">
              <GripVertical size={8} className="text-black rotate-90" />
            </div>
          </div>
        );
      }
    });
    // Render ALL benches at the wall level, anchored to the bottom
    const wallBenches = surface.benches || [];
    wallBenches.forEach(bench => {
      const benchRef = React.createRef();
      elements.push(
        <div key={`bench-${bench.id}`}
          ref={benchRef}
          data-testid={`bench-${bench.id}`}
          className={`absolute cursor-grab active:cursor-grabbing z-20 transition-shadow ${selectedElement?.id === bench.id ? 'ring-2 ring-orange-400' : 'hover:ring-1 hover:ring-orange-300/50'}`}
          style={{
            left: `${bench.x}%`, bottom: '0%',
            width: `${bench.w}%`, height: `${bench.h}%`,
            boxShadow: '0 -2px 8px rgba(0,0,0,0.3), inset 0 1px 0 rgba(255,255,255,0.2)',
            border: bench.trim === 'schluter' ? '3px solid #c0c0c0' : bench.trim === 'bullnose' ? '3px solid #e8e0d4' : bench.trim === 'pencil' ? '2px solid #8b7355' : '2px solid rgba(100,80,60,0.5)',
            background: '#ddd8d0', borderRadius: '2px 2px 0 0',
          }}
          onMouseDown={e => {
            e.stopPropagation(); e.preventDefault();
            if (selectedItem) {
              if (surface.id && onApplyTileToElement) onApplyTileToElement(surface.id, 'bench', bench.id, selectedItem);
            } else {
              const rect = e.currentTarget.parentElement?.getBoundingClientRect();
              if (rect) handleElementDragStart(e, 'bench', bench, surface.id, rect);
            }
          }}>
          {bench.material?.image && bench.material?.pattern && (
            <TilePatternCanvas pattern={bench.material.pattern} imageUrl={bench.material.image}
              tileCrop={bench.material.tile_crop} groutColor={bench.material.grout_color}
              tileOrientation={bench.material.tile_orientation} tileScale={bench.material.tile_scale} />
          )}
          <div className="absolute top-0 left-0 right-0 h-[4px] bg-gradient-to-b from-white/30 to-transparent pointer-events-none" />
          <div className="absolute bottom-0 left-0 right-0 px-1 py-0.5 text-[7px] font-black text-white/80 bg-black/60 text-center pointer-events-none">
            BENCH {bench.width_inches ? `${bench.width_inches} × ${bench.height_inches || ''}` : ''}
          </div>
          {selectedElement?.id === bench.id && <>
            <div className="absolute -top-1 -left-1 w-2.5 h-2.5 bg-orange-400 border border-white rounded-full cursor-nw-resize z-30"
              onMouseDown={e => { e.stopPropagation(); e.preventDefault(); const rect = e.currentTarget.parentElement?.parentElement?.getBoundingClientRect(); if (rect) handleElementDragStart(e, 'bench-resize-tl', bench, surface.id, rect); }} />
            <div className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-orange-400 border border-white rounded-full cursor-ne-resize z-30"
              onMouseDown={e => { e.stopPropagation(); e.preventDefault(); const rect = e.currentTarget.parentElement?.parentElement?.getBoundingClientRect(); if (rect) handleElementDragStart(e, 'bench-resize-tr', bench, surface.id, rect); }} />
            <div className="absolute -bottom-1 -left-1 w-2.5 h-2.5 bg-orange-400 border border-white rounded-full cursor-sw-resize z-30"
              onMouseDown={e => { e.stopPropagation(); e.preventDefault(); const rect = e.currentTarget.parentElement?.parentElement?.getBoundingClientRect(); if (rect) handleElementDragStart(e, 'bench-resize-bl', bench, surface.id, rect); }} />
            <div className="absolute -bottom-1 -right-1 w-2.5 h-2.5 bg-orange-400 border border-white rounded-full cursor-se-resize z-30"
              onMouseDown={e => { e.stopPropagation(); e.preventDefault(); const rect = e.currentTarget.parentElement?.parentElement?.getBoundingClientRect(); if (rect) handleElementDragStart(e, 'bench-resize-br', bench, surface.id, rect); }} />
          </>}
        </div>
      );
    });
    return elements;
  };

  const renderSimpleSurface = (surface, label) => {
    if (!surface) return null;
    const mat = (surface.materials || [])[0];
    const hasImage = mat?.image;
    const surfFixtures = surface.fixtures || [];
    return (
      <div className="absolute inset-0 overflow-hidden cursor-pointer group"
        onClick={(e) => { e.stopPropagation(); handleCeilingFloorClick(surface); }}
        data-testid={`surface-${surface.surface_type}`}
        style={{ pointerEvents: 'auto' }}>
        {hasImage ? (
          <TilePatternCanvas pattern={mat.pattern || 'stacked_horizontal'} imageUrl={mat.image} tileCrop={mat.tile_crop}
            groutColor={mat.grout_color} tileOrientation={mat.tile_orientation} tileScale={mat.tile_scale} />
        ) : (
          <div className="absolute inset-0" style={{ background: surface.surface_type === 'ceiling' ? '#c8c5bf' : '#504c46' }} />
        )}
        {surfFixtures.map(f => <FixtureOverlay key={f.id} fixture={f} isSelected={selectedElement?.id === f.id}
          onDragStart={handleElementDragStart} />)}
        <div className="absolute top-1 left-1/2 -translate-x-1/2 px-1.5 py-0.5 rounded text-[7px] font-black text-white/70 bg-black/40 z-10 pointer-events-none">{label}</div>
        {hasImage && (
          <div className="absolute bottom-0 left-0 right-0 flex items-center justify-end px-1 py-0.5 opacity-0 group-hover:opacity-100 transition-opacity z-10"
            style={{ background: 'rgba(0,0,0,0.65)' }}>
            <button onClick={e => { e.stopPropagation(); mat && onCropTile(surface.id, mat.id, mat.image, mat.tile_crop); }}
              className="px-1 py-0.5 rounded text-[8px] font-black text-orange-300 bg-black/80 mr-1" data-testid={`crop-btn-${surface.surface_type}`}><Crop size={8} className="inline mr-0.5" />CROP</button>
            <button onClick={e => { e.stopPropagation(); setPatternPicker(patternPicker?.zoneId === '_surface' && patternPicker?.surfaceId === surface.id ? null : { surfaceId: surface.id, zoneId: '_surface' }); }}
              className="px-1 py-0.5 rounded text-[8px] font-black text-amber-300 bg-black/80" data-testid={`pattern-btn-${surface.surface_type}`}>PATTERN</button>
          </div>
        )}
        {!hasImage && (
          <div className={`absolute inset-0 flex items-center justify-center transition-all ${selectedItem ? 'bg-amber-400/10 border-2 border-dashed border-amber-400/40' : ''}`}>
            <span className="text-white/30 text-[9px] font-bold">{selectedItem ? 'CLICK TO PLACE' : ''}</span>
          </div>
        )}
      </div>
    );
  };

  const pickerSurface = patternPicker ? surfaces.find(s => s.id === patternPicker.surfaceId) : null;
  const pickerMat = pickerSurface ? (pickerSurface.materials || []).find(m => m.position_label === patternPicker?.zoneId) : null;

  return (
    <div data-testid="shower-3d-view">
      {/* 3D SHOWER ENCLOSURE — one-point perspective box using clip-path */}
      <div style={{ width: '100%', height: '720px', position: 'relative', background: '#0a0a10', borderRadius: '12px', overflow: 'hidden' }}>

        {/* CEILING — trapezoid */}
        <div style={{
          position: 'absolute', left: 0, top: 0, width: '100%', height: '10%',
          clipPath: 'polygon(0% 0%, 100% 0%, 80% 100%, 20% 100%)',
          background: '#c8c5bf', zIndex: 1,
        }}>
          {renderSimpleSurface(ceiling, ceiling?.name || 'CEILING')}
        </div>

        {/* LEFT WALL — trapezoid */}
        <div style={{
          position: 'absolute', left: 0, top: 0, width: '21%', height: '100%',
          clipPath: 'polygon(0% 0%, 100% 10%, 100% 82%, 0% 100%)',
          background: '#3a3832', zIndex: 2,
        }}>
          <div className="absolute inset-0" style={{ background: 'linear-gradient(to right, rgba(0,0,0,0.25), rgba(0,0,0,0.05))' }} />
          {renderWallFace(leftWall, true)}
          <div className="absolute top-[12%] left-1 px-1.5 py-0.5 rounded text-[6px] font-black text-white/30 z-10 pointer-events-none">{leftWall?.name || 'LEFT WALL'}</div>
        </div>

        {/* BACK WALL — flat center rectangle */}
        <div data-surface-id={backWall?.id} style={{
          position: 'absolute', left: '20%', top: '10%', width: '60%', height: '72%',
          background: '#4a4842', border: '2px solid #333', zIndex: 5,
          boxShadow: '0 4px 30px rgba(0,0,0,0.6), inset 0 0 40px rgba(0,0,0,0.08)',
        }}>
          {renderWallFace(backWall, false)}
          <div className="absolute top-2 left-1/2 -translate-x-1/2 px-2 py-0.5 rounded text-[7px] font-black text-white/30 z-10 pointer-events-none">{backWall?.name || 'BACK WALL'}</div>
          {/* Zone dimension labels on the right edge */}
          {backWall && getZoneConfig(backWall).reduce((acc, zone) => {
            const top = acc.top;
            acc.items.push(
              <div key={zone.id} className="absolute right-0 flex items-center z-20" style={{ top: `${top}%`, height: `${zone.height}%` }}>
                <div className="flex flex-col items-end pr-1 gap-0.5">
                  {editingDim?.surfaceId === backWall.id && editingDim?.zoneId === zone.id ? (
                    <input autoFocus value={dimValue} onChange={e => setDimValue(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter') { onEditDimension(backWall.id, zone.id, dimValue); setEditingDim(null); } if (e.key === 'Escape') setEditingDim(null); }}
                      onBlur={() => { onEditDimension(backWall.id, zone.id, dimValue); setEditingDim(null); }}
                      className="w-16 px-1 py-0 text-[9px] font-mono bg-black text-amber-300 border border-amber-400/60 rounded text-right"
                      onClick={e => e.stopPropagation()} data-testid={`dim-input-${zone.id}`} />
                  ) : (
                    <button onClick={e => { e.stopPropagation(); setEditingDim({ surfaceId: backWall.id, zoneId: zone.id }); setDimValue(zone.dimension || ''); }}
                      className="px-1.5 py-0.5 rounded text-[8px] font-mono text-amber-300/70 bg-black/50 hover:bg-black/70 border border-transparent hover:border-amber-400/40 transition-all"
                      data-testid={`dim-label-${zone.id}`}>
                      {zone.dimension || 'SIZE'}
                    </button>
                  )}
                </div>
              </div>
            );
            acc.top += zone.height;
            return acc;
          }, { top: 0, items: [] }).items}
        </div>

        {/* RIGHT WALL — trapezoid */}
        <div style={{
          position: 'absolute', right: 0, top: 0, width: '21%', height: '100%',
          clipPath: 'polygon(0% 10%, 100% 0%, 100% 100%, 0% 82%)',
          background: '#3a3832', zIndex: 2,
        }}>
          <div className="absolute inset-0" style={{ background: 'linear-gradient(to left, rgba(0,0,0,0.25), rgba(0,0,0,0.05))' }} />
          {renderWallFace(rightWall, true)}
          <div className="absolute top-[12%] right-1 px-1.5 py-0.5 rounded text-[6px] font-black text-white/30 z-10 pointer-events-none">{rightWall?.name || 'RIGHT WALL'}</div>
        </div>

        {/* FLOOR — trapezoid, much larger and visible */}
        <div style={{
          position: 'absolute', left: 0, bottom: 0, width: '100%', height: '18%',
          clipPath: 'polygon(20% 0%, 80% 0%, 100% 100%, 0% 100%)',
          background: '#504c46', zIndex: 6,
        }}>
          {renderSimpleSurface(floor, floor?.name || 'FLOOR')}
        </div>

        {/* Corner shadow lines for depth */}
        <svg className="absolute inset-0 pointer-events-none z-20" viewBox="0 0 100 100" preserveAspectRatio="none">
          {/* Top-left corner */}
          <line x1="0" y1="0" x2="20" y2="10" stroke="rgba(0,0,0,0.5)" strokeWidth="0.3" />
          {/* Top-right corner */}
          <line x1="100" y1="0" x2="80" y2="10" stroke="rgba(0,0,0,0.5)" strokeWidth="0.3" />
          {/* Bottom-left corner */}
          <line x1="0" y1="100" x2="20" y2="82" stroke="rgba(0,0,0,0.5)" strokeWidth="0.3" />
          {/* Bottom-right corner */}
          <line x1="100" y1="100" x2="80" y2="82" stroke="rgba(0,0,0,0.5)" strokeWidth="0.3" />
          {/* Back wall vertical edges */}
          <line x1="20" y1="10" x2="20" y2="82" stroke="rgba(0,0,0,0.4)" strokeWidth="0.25" />
          <line x1="80" y1="10" x2="80" y2="82" stroke="rgba(0,0,0,0.4)" strokeWidth="0.25" />
          {/* Back wall horizontal edges */}
          <line x1="20" y1="10" x2="80" y2="10" stroke="rgba(0,0,0,0.3)" strokeWidth="0.2" />
          <line x1="20" y1="82" x2="80" y2="82" stroke="rgba(0,0,0,0.3)" strokeWidth="0.2" />
        </svg>
      </div>

      {/* PATTERN PICKER + CONTROLS */}
      {patternPicker && pickerMat && (
        <div className="mt-3 p-4 rounded-xl border-2 border-amber-400/30 shadow-2xl" style={{ background: 'rgba(0,0,0,0.97)' }} data-testid="pattern-picker">
          <div className="flex justify-between items-center mb-3">
            <span className="text-sm font-black text-amber-400">
              PATTERN FOR: <span className="text-white">{patternPicker.zoneId === '_surface' ? (pickerSurface?.surface_type === 'ceiling' ? 'Ceiling' : 'Floor') : (getZoneConfig(pickerSurface).find(z => z.id === patternPicker.zoneId)?.label || patternPicker.zoneId)}</span>
            </span>
            <button onClick={() => setPatternPicker(null)} className="text-white/40 hover:text-white"><X size={18} /></button>
          </div>
          {/* Grout + Orientation + Tile Size row */}
          <div className="flex items-center gap-4 mb-3 pb-3 border-b border-white/10 flex-wrap">
            <div className="flex items-center gap-2">
              <Palette size={12} className="text-white/40" />
              <span className="text-[10px] font-black text-white/50">GROUT:</span>
              {GROUT_COLORS.map(gc => (
                <button key={gc.value} onClick={() => onChangeGrout(patternPicker.surfaceId, pickerMat.id, gc.value)}
                  className={`w-5 h-5 rounded-full border-2 transition-all ${pickerMat.grout_color === gc.value ? 'border-amber-400 scale-125' : 'border-white/20 hover:border-white/40'}`}
                  style={{ background: gc.value }} title={gc.label} data-testid={`grout-${gc.label.toLowerCase().replace(' ', '-')}`} />
              ))}
            </div>
            <div className="flex items-center gap-2">
              <RotateCw size={12} className="text-white/40" />
              <span className="text-[10px] font-black text-white/50">TILE:</span>
              <button onClick={() => onChangeOrientation(patternPicker.surfaceId, pickerMat.id, 'horizontal')}
                className={`px-2 py-1 rounded text-[9px] font-black ${pickerMat.tile_orientation !== 'vertical' ? 'bg-amber-600 text-white' : 'text-white/30 border border-white/10'}`}
                data-testid="orient-horizontal">HORIZONTAL</button>
              <button onClick={() => onChangeOrientation(patternPicker.surfaceId, pickerMat.id, 'vertical')}
                className={`px-2 py-1 rounded text-[9px] font-black ${pickerMat.tile_orientation === 'vertical' ? 'bg-amber-600 text-white' : 'text-white/30 border border-white/10'}`}
                data-testid="orient-vertical">VERTICAL</button>
            </div>
            <div className="flex items-center gap-2">
              <Ruler size={12} className="text-white/40" />
              <span className="text-[10px] font-black text-white/50">TILE SIZE:</span>
              <input type="range" min={50} max={500} step={10}
                value={Math.round((pickerMat.tile_scale || 1) * 100)}
                onChange={e => onChangeTileScale(patternPicker.surfaceId, pickerMat.id, Number(e.target.value) / 100)}
                className="w-24 accent-amber-500" data-testid="tile-size-slider" />
              <span className="text-[9px] font-mono text-amber-300 w-8">{Math.round((pickerMat.tile_scale || 1) * 100)}%</span>
            </div>
          </div>
          <div className="grid grid-cols-5 gap-2">
            {TILE_PATTERNS.map(p => (
              <button key={p.value} onClick={() => onChangePattern(patternPicker.surfaceId, pickerMat.id, p.value)}
                className={`flex flex-col items-center p-2 rounded-lg border-2 transition-all ${pickerMat.pattern === p.value ? 'border-amber-400 bg-amber-400/10 scale-105' : 'border-white/10 hover:border-white/30 hover:bg-white/5'}`}
                data-testid={`pattern-${p.value}`}>
                <PatternThumb pattern={p.value} size={52} />
                <span className={`text-[7px] font-black mt-1 tracking-wider ${pickerMat.pattern === p.value ? 'text-amber-400' : 'text-white/60'}`}>{p.label}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* SELECTED ELEMENT CONTROLS */}
      {selectedElement && (
        <SelectedElementPanel
          element={selectedElement}
          surfaces={surfaces}
          onDelete={onDeleteElement}
          onUpdate={onUpdateElement}
          onDeselect={() => onSelectElement(null)}
          selectedItem={selectedItem}
          onApplyTile={onApplyTileToElement}
        />
      )}
    </div>
  );
};


/* ==================== SELECTED ELEMENT PANEL ==================== */
const TRIM_OPTIONS = [
  { value: 'none', label: 'No Trim' },
  { value: 'schluter', label: 'Schluter (Metal)' },
  { value: 'bullnose', label: 'Bullnose' },
  { value: 'pencil', label: 'Pencil Liner' },
  { value: 'quarter_round', label: 'Quarter Round' },
];

const SelectedElementPanel = ({ element, surfaces, onDelete, onUpdate, onDeselect, selectedItem, onApplyTile }) => {
  if (!element) return null;
  let found = null, foundSurface = null;
  for (const s of surfaces) {
    if (element.type === 'niche') found = (s.niches || []).find(n => n.id === element.id);
    else if (element.type === 'bench') found = (s.benches || []).find(b => b.id === element.id);
    else if (element.type === 'fixture') found = (s.fixtures || []).find(f => f.id === element.id);
    if (found) { foundSurface = s; break; }
  }
  if (!found || !foundSurface) return null;
  const isNicheOrBench = element.type === 'niche' || element.type === 'bench';
  const hasTile = found.material?.image;

  return (
    <div className="mt-3 p-4 rounded-xl border-2 shadow-2xl" data-testid="element-panel"
      style={{ background: 'rgba(0,0,0,0.97)', borderColor: element.type === 'niche' ? 'rgba(0,200,255,0.3)' : element.type === 'bench' ? 'rgba(255,165,0,0.3)' : 'rgba(100,160,255,0.3)' }}>
      <div className="flex justify-between items-center mb-3">
        <span className="text-sm font-black" style={{ color: element.type === 'niche' ? '#00c8ff' : element.type === 'bench' ? '#ffa500' : '#64a0ff' }}>
          {element.type.toUpperCase()} — {isNicheOrBench ? 'Drag corners to resize' : 'Drag to move'}
        </span>
        <div className="flex gap-2">
          {selectedItem && isNicheOrBench && (
            <button onClick={() => onApplyTile(foundSurface.id, element.type, element.id, selectedItem)}
              className="px-2 py-1 rounded text-[9px] font-black text-green-400 border border-green-400/30 hover:bg-green-400/10 animate-pulse" data-testid="apply-tile-to-element-btn">
              APPLY {selectedItem.name?.substring(0, 15)}
            </button>
          )}
          <button onClick={() => onDelete(foundSurface.id, element.type, element.id)} className="px-2 py-1 rounded text-[9px] font-black text-red-400 border border-red-400/30 hover:bg-red-400/10" data-testid="delete-element-btn">
            <Trash2 size={10} className="inline mr-1" />DELETE
          </button>
          <button onClick={onDeselect} className="text-white/40 hover:text-white"><X size={16} /></button>
        </div>
      </div>
      {/* Position & size controls */}
      <div className="grid grid-cols-4 gap-2 mb-3">
        {['x', 'y'].concat(isNicheOrBench ? ['w', 'h'] : []).map(field => (
          <div key={field}>
            <label className="text-[8px] font-black text-white/40 uppercase">{field === 'x' ? 'LEFT %' : field === 'y' ? 'TOP %' : field === 'w' ? 'WIDTH %' : 'HEIGHT %'}</label>
            <input type="number" min={0} max={100} value={Math.round(found[field] || 0)}
              onChange={e => onUpdate(foundSurface.id, element.type, element.id, { [field]: Number(e.target.value) })}
              className="w-full px-2 py-1 rounded text-[10px] font-mono bg-black border border-white/20 text-white" data-testid={`element-${field}`} />
          </div>
        ))}
      </div>
      {/* Bench/Niche specific: Dimension (inches) */}
      {isNicheOrBench && (
        <div className="grid grid-cols-2 gap-2 mb-3">
          <div>
            <label className="text-[8px] font-black text-white/40 uppercase">WIDTH (inches)</label>
            <input type="text" placeholder='e.g. 36"' value={found.width_inches || ''}
              onChange={e => onUpdate(foundSurface.id, element.type, element.id, { width_inches: e.target.value })}
              className="w-full px-2 py-1 rounded text-[10px] font-mono bg-black border border-white/20 text-white" data-testid="element-width-inches" />
          </div>
          <div>
            <label className="text-[8px] font-black text-white/40 uppercase">HEIGHT (inches)</label>
            <input type="text" placeholder='e.g. 18"' value={found.height_inches || ''}
              onChange={e => onUpdate(foundSurface.id, element.type, element.id, { height_inches: e.target.value })}
              className="w-full px-2 py-1 rounded text-[10px] font-mono bg-black border border-white/20 text-white" data-testid="element-height-inches" />
          </div>
        </div>
      )}
      {/* Trim / Schluter */}
      {isNicheOrBench && (
        <div className="mb-3">
          <label className="text-[8px] font-black text-white/40 uppercase block mb-1">EDGE TRIM / SCHLUTER</label>
          <div className="flex gap-1 flex-wrap">
            {TRIM_OPTIONS.map(t => (
              <button key={t.value} onClick={() => onUpdate(foundSurface.id, element.type, element.id, { trim: t.value })}
                className={`px-2 py-1 rounded text-[9px] font-black transition-all ${(found.trim || 'none') === t.value ? 'bg-amber-600 text-white' : 'text-white/30 border border-white/10 hover:border-white/30'}`}
                data-testid={`trim-${t.value}`}>
                {t.label}
              </button>
            ))}
          </div>
        </div>
      )}
      {/* Tile info on element */}
      {hasTile && (
        <div className="pt-2 border-t border-white/10">
          <div className="text-[8px] font-black text-green-400/70 mb-1">TILE APPLIED</div>
          <div className="flex items-center gap-2">
            <img src={found.material.image} alt="" className="w-8 h-8 rounded object-cover border border-white/20" />
            <span className="text-[9px] text-white/50">{found.material.pattern || 'No pattern'}</span>
          </div>
        </div>
      )}
    </div>
  );
};


/* ==================== MEASUREMENT CANVAS ==================== */
// The old detached 2D measurement box was removed: its lines were stored as
// percentages of an abstract canvas, unrelated to the room, so they could not
// mean anything. Dimensions now live on the 3D model (see RoomVisualizer3D).
// Existing measurement_lines data is left untouched in the database.


/* ==================== MAIN COMPONENT ==================== */
const RoomFinishSchedule = ({ projectId, roomId, roomName, onClose }) => {
  const [schedules, setSchedules] = useState([]);
  const [activeSchedule, setActiveSchedule] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const autoCreatedRef = useRef(false);
  const [newName, setNewName] = useState('');
  const [newType, setNewType] = useState('tile');
  const [saving, setSaving] = useState(false);
  const [roomItems, setRoomItems] = useState([]);
  // Anything found through the search is added to the squares above, so it can
  // be dragged onto a wall like everything else. Selecting it alone still works
  // (click the wall) - this just means you are never forced to leave the flow to
  // "add" something to the list first.
  const [searchPicks, setSearchPicks] = useState([]);
  const [paletteGroup, setPaletteGroup] = useState('finish');
  const [showLibrary, setShowLibrary] = useState(false);  // finishes first by default

  // The project header, tabs and palette push the 3D view ~780px down the page,
  // so opening a room used to show everything except the room. Bring the walls
  // into view as soon as a schedule is opened.
  const vizRef = React.useRef(null);
  const scrolledForRef = React.useRef(null);

  React.useEffect(() => {
    const id = activeSchedule?.id;
    if (!id || !vizRef.current) return;
    // Once per schedule, so it does not fight the user's own scrolling.
    if (scrolledForRef.current === id) return;
    scrolledForRef.current = id;
    vizRef.current.scrollIntoView({ block: 'center', inline: 'nearest' });
  }, [activeSchedule?.id]);

  // Palette order: finishes lead, then plumbing, lighting, furniture, decor.
  // Only groups that actually have items are offered as filters.
  const paletteBuckets = React.useMemo(() => {
    const buckets = new Map();
    // Searched items lead, so what you just looked up is the first square.
    [...searchPicks, ...roomItems].forEach(item => {
      const key = classifyPaletteItem(item);
      if (!buckets.has(key)) buckets.set(key, []);
      buckets.get(key).push(item);
    });
    return buckets;
  }, [roomItems, searchPicks]);

  const availableGroups = React.useMemo(
    () => [...PALETTE_GROUPS, PALETTE_OTHER].filter(g => (paletteBuckets.get(g.key) || []).length > 0),
    [paletteBuckets]
  );

  // Land on finishes when this room has any; otherwise the first group that exists.
  const activeGroup = (paletteGroup === 'all' && availableGroups.length > 1)
      || availableGroups.some(g => g.key === paletteGroup)
    ? paletteGroup
    : (availableGroups[0]?.key || 'finish');

  const itemsWithImages = React.useMemo(() => {
    if (activeGroup === 'all') {
      return availableGroups.flatMap(g => paletteBuckets.get(g.key) || []);
    }
    return paletteBuckets.get(activeGroup) || [];
  }, [activeGroup, availableGroups, paletteBuckets]);
  const [selectedItem, setSelectedItem] = useState(null);
  const [placementMode, setPlacementMode] = useState(null);
  const [fixtureCategory, setFixtureCategory] = useState('fixture');
  const [displayMode, setDisplayMode] = useState('sheet'); // 'full' or 'sheet'
  const [selectedPattern, setSelectedPattern] = useState('stacked_horizontal');

  // Update display mode + pattern on ALL wall materials
  const updateAllWallMaterials = (updates) => {
    updateSchedule(prev => ({
      ...prev,
      surfaces: prev.surfaces.map(s => {
        if (s.surface_type !== 'wall') return s;
        return { ...s, materials: (s.materials || []).map(m => ({ ...m, ...updates })) };
      })
    }));
  };
  const [selectedElement, setSelectedElement] = useState(null);
  const saveTimeoutRef = useRef(null);
  const [cropModal, setCropModal] = useState(null);
  const pendingPlaceRef = useRef(null);

  /** Create a schedule and make it the active one. Shared by the automatic
   *  build on open and the NEW dialog, so both behave identically. */
  const createSchedule = useCallback(async (name, type = 'tile') => {
    try {
      const res = await fetch(`${API_URL}/api/projects/${projectId}/rooms/${roomId}/finish-schedules`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ schedule_type: type, name: (name || 'Room').trim() })
      });
      if (!res.ok) throw new Error(`create failed (${res.status})`);
      const schedule = await res.json();
      schedule.surfaces = ensureAllSurfaces(schedule.surfaces);
      schedule.objects = schedule.objects || [];
      schedule.annotations = schedule.annotations || [];
      setSchedules(prev => [...prev, schedule]);
      setActiveSchedule(schedule);
      setLoading(false);
      return schedule;
    } catch (err) {
      console.error('Could not create the room:', err);
      return null;
    }
  }, [projectId, roomId]);

  const fetchSchedules = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/projects/${projectId}/rooms/${roomId}/finish-schedules`);
      const data = await res.json();
      const enhanced = data.map(s => ({
        ...s,
        surfaces: ensureAllSurfaces(s.surfaces),
        objects: s.objects || [],
        annotations: s.annotations || [],
      }));
      setSchedules(enhanced);
      if (enhanced.length > 0 && !activeSchedule) {
        setActiveSchedule(enhanced[0]);
      } else if (enhanced.length === 0 && !autoCreatedRef.current) {
        // Opening a room used to show "No finish schedules yet — click NEW",
        // with no 3D room and therefore no wall to drag a finish onto. The room
        // is what you came for, so it is built on arrival, named after the room
        // itself. Extra schedules (a shower inside a bathroom, say) are still
        // added with NEW.
        autoCreatedRef.current = true;
        const created = await createSchedule(roomName || 'Room', 'tile');
        if (created) return;
      }
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [projectId, roomId, activeSchedule, roomName, createSchedule]);

  const fetchRoomItems = useCallback(async () => {
    try {
      // Pull products from FF&E (primary) and merge Checklist items for the same
      // room by name, so the palette shows ALL products — image optional.
      const [ffeRes, clRes] = await Promise.all([
        fetch(`${API_URL}/api/projects/${projectId}?sheet_type=ffe`),
        fetch(`${API_URL}/api/projects/${projectId}?sheet_type=checklist`),
      ]);
      const ffe = await ffeRes.json();
      let cl = { rooms: [] };
      try { cl = await clRes.json(); } catch (e) { /* checklist optional */ }
      const room = (ffe.rooms || []).find(r => r.id === roomId);
      const items = [];
      const seen = new Set();
      const pushItem = (item, catName, subName, source) => {
        const key = (item.name || '').toLowerCase();
        if (!key || seen.has(key)) return;
        items.push({ ...item, categoryName: catName, subcategoryName: subName, source, _img: getItemImage(item) });
        seen.add(key);
      };
      // 1) Current room from FF&E (primary).
      (room?.categories || []).forEach(cat => (cat.subcategories || []).forEach(sub =>
        (sub.items || []).forEach(item => pushItem(item, cat.name, sub.name, 'FF&E'))));
      // 2) Merge the same room's Checklist items (by room name).
      const clRoom = (cl.rooms || []).find(r => (r.name || '').toLowerCase() === (room?.name || '').toLowerCase());
      (clRoom?.categories || []).forEach(cat => (cat.subcategories || []).forEach(sub =>
        (sub.items || []).forEach(item => pushItem(item, cat.name, sub.name, 'Checklist'))));
      // 3) PLUMBING SYNC — pull every plumbing-classified item from ALL other
      //    rooms and the project's Whole Home group (both sheets), so no fixture
      //    is missed. Non-plumbing items from other rooms are ignored.
      const addPlumbingFrom = (sheet, sheetLabel) => {
        (sheet.rooms || []).forEach(r => (r.categories || []).forEach(cat => (cat.subcategories || []).forEach(sub =>
          (sub.items || []).forEach(item => {
            if (classifyPaletteItem({ categoryName: cat.name, subcategoryName: sub.name, name: item.name }) !== 'plumbing') return;
            pushItem(item, cat.name, sub.name, r.id === roomId ? 'FF&E' : `${sheetLabel} · ${r.name || 'Room'}`);
          }))));
      };
      addPlumbingFrom(ffe, 'FF&E');
      addPlumbingFrom(cl, 'Checklist');
      setRoomItems(items);
    } catch (err) { console.error(err); }
  }, [projectId, roomId]);

  useEffect(() => { fetchSchedules(); fetchRoomItems(); }, [fetchSchedules, fetchRoomItems]);

  const save = useCallback(async (schedule) => {
    if (!schedule) return;
    try {
      const res = await fetch(`${API_URL}/api/projects/${projectId}/finish-schedules/${schedule.id}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          surfaces: schedule.surfaces,
          measurement_lines: schedule.measurement_lines || [],
          room_config: schedule.room_config || null,
          objects: schedule.objects || [],
          annotations: schedule.annotations || [],
        })
      });
      const updated = await res.json();
      if (updated.surfaces) updated.surfaces = ensureAllSurfaces(updated.surfaces);
      setSchedules(prev => prev.map(s => s.id === updated.id ? updated : s));
      return updated;
    } catch (err) { console.error(err); }
  }, [projectId]);

  const debouncedSave = useCallback((schedule) => {
    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    saveTimeoutRef.current = setTimeout(() => save(schedule), 600);
  }, [save]);

  const updateSchedule = useCallback((updater) => {
    setActiveSchedule(prev => {
      const updated = typeof updater === 'function' ? updater(prev) : updater;
      debouncedSave(updated);
      return updated;
    });
  }, [debouncedSave]);

  const handleCreate = async () => {
    const name = newName.trim() || roomName || 'Room';
    setSaving(true);
    const created = await createSchedule(name, newType);
    setSaving(false);
    if (created) { setShowCreate(false); setNewName(''); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this schedule?')) return;
    try {
      await fetch(`${API_URL}/api/projects/${projectId}/finish-schedules/${id}`, { method: 'DELETE' });
      setSchedules(prev => prev.filter(s => s.id !== id));
      if (activeSchedule?.id === id) setActiveSchedule(null);
    } catch (err) { console.error(err); }
  };

  const findExistingCropForItem = (itemId) => {
    if (!activeSchedule) return null;
    for (const surface of activeSchedule.surfaces) {
      for (const mat of (surface.materials || [])) {
        if (mat.item_id === itemId && mat.tile_crop && mat.tile_crop.w > 0) return mat.tile_crop;
      }
    }
    return null;
  };

  const handlePlaceItem = (surfaceId, zoneId, item) => {
    if (!activeSchedule || !item) return;
    const existingCrop = findExistingCropForItem(item.id);
    if (existingCrop) {
      placeItemWithCrop(surfaceId, zoneId, item, existingCrop);
    } else if (item._img) {
      pendingPlaceRef.current = { surfaceId, zoneId, item };
      setCropModal({ imageUrl: item._img, existingCrop: null, mode: 'place' });
    } else {
      placeItemWithCrop(surfaceId, zoneId, item, null);
    }
  };

  const placeItemWithCrop = (surfaceId, zoneId, item, tileCrop) => {
    updateSchedule(prev => {
      const targetSurface = prev.surfaces.find(s => s.id === surfaceId);
      const isBackWall = targetSurface?.name?.toLowerCase().includes('back');
      const makeMat = (posLabel) => ({
        id: crypto.randomUUID(), item_id: item.id, name: item.name || '', vendor: item.vendor || '',
        sku: item.sku || '', size: item.size || '', color: item.finish_color || item.color || '',
        image: item._img || '', link: item.link || '', position_label: posLabel,
        tile_crop: tileCrop, grout_color: '#4a4035', tile_orientation: 'horizontal', tile_scale: 1.0,
        display_mode: displayMode || 'sheet',
        pattern: selectedPattern || 'stacked_horizontal',
      });

      const fillAllZones = (surface) => {
        const zones = surface.zone_config || DEFAULT_ZONE_CONFIG;
        let mats = [...(surface.materials || [])];
        // Overwrite ALL zones with the new material
        zones.forEach(z => {
          const idx = mats.findIndex(m => m.position_label === z.id);
          if (idx >= 0) { mats[idx] = makeMat(z.id); }
          else { mats.push(makeMat(z.id)); }
        });
        return mats;
      };

      return {
        ...prev,
        surfaces: prev.surfaces.map(s => {
          if (s.id === surfaceId) {
            return { ...s, materials: fillAllZones(s) };
          }
          // Auto-fill side walls when placing on back wall
          if (isBackWall && s.surface_type === 'wall' && s.id !== surfaceId) {
            return { ...s, materials: fillAllZones(s) };
          }
          return s;
        })
      };
    });
  };

  const handlePlaceCeilingFloor = (surfaceId, item) => {
    if (!activeSchedule || !item) return;
    const existingCrop = findExistingCropForItem(item.id);
    if (existingCrop) {
      placeCeilingFloorWithCrop(surfaceId, item, existingCrop);
    } else if (item._img) {
      pendingPlaceRef.current = { surfaceId, zoneId: '_surface', item };
      setCropModal({ imageUrl: item._img, existingCrop: null, mode: 'place_surface' });
    } else {
      placeCeilingFloorWithCrop(surfaceId, item, null);
    }
  };

  const placeCeilingFloorWithCrop = (surfaceId, item, tileCrop) => {
    updateSchedule(prev => ({
      ...prev,
      surfaces: prev.surfaces.map(s => {
        if (s.id !== surfaceId) return s;
        return { ...s, materials: [{
          id: crypto.randomUUID(), item_id: item.id, name: item.name || '',
          vendor: item.vendor || '', image: item._img || '', position_label: '_surface',
          pattern: 'stacked_horizontal', tile_crop: tileCrop, grout_color: '#4a4035', tile_orientation: 'horizontal', tile_scale: 1.0,
        }] };
      })
    }));
  };

  const handlePlaceFixture = (surfaceId, zoneId, item, xPct, yPct) => {
    updateSchedule(prev => ({
      ...prev,
      surfaces: prev.surfaces.map(s => {
        if (s.id !== surfaceId) return s;
        return { ...s, fixtures: [...(s.fixtures || []), {
          id: crypto.randomUUID(), item_id: item.id, name: item.name || '',
          image: item._img || '', zone_id: zoneId, x: Math.round(xPct), y: Math.round(yPct),
          category: fixtureCategory,
        }] };
      })
    }));
    setPlacementMode(null);
  };

  /** Drop every plumbing item from the palette into the room's scene at once,
   *  staggered across the first wall. Skips any already-placed fixtures. */
  const placeAllPlumbing = () => {
    const plumbing = paletteBuckets.get('plumbing') || [];
    if (!plumbing.length || !activeSchedule) return;
    const wall = (activeSchedule.surfaces || []).find(s => s.surface_type === 'wall');
    if (!wall) { window.alert('No wall in this room to place fixtures on.'); return; }
    const placedIds = new Set();
    (activeSchedule.surfaces || []).forEach(s => (s.fixtures || []).forEach(f => placedIds.add(f.item_id)));
    const toPlace = plumbing.filter(item => !placedIds.has(item.id));
    if (!toPlace.length) { window.alert('All plumbing fixtures are already placed in this room.'); return; }
    updateSchedule(prev => ({
      ...prev,
      surfaces: prev.surfaces.map(s => {
        if (s.id !== wall.id) return s;
        const base = (s.fixtures || []).length;
        const additions = toPlace.map((item, i) => ({
          id: crypto.randomUUID(), item_id: item.id, name: item.name || '',
          image: item._img || '', zone_id: '_surface',
          x: 12 + ((base + i) % 6) * 14, y: 28 + Math.floor((base + i) / 6) * 22,
          category: 'fixture',
        }));
        return { ...s, fixtures: [...(s.fixtures || []), ...additions] };
      }),
    }));
    setSelectedItem(null);
    setPlacementMode(null);
  };

  const handleAddNiche = (surfaceId, zoneId, xPct, yPct) => {
    updateSchedule(prev => ({
      ...prev,
      surfaces: prev.surfaces.map(s => {
        if (s.id !== surfaceId) return s;
        return { ...s, niches: [...(s.niches || []), {
          id: crypto.randomUUID(), zone_id: zoneId,
          x: Math.max(0, Math.min(70, xPct - 15)), y: Math.max(0, Math.min(75, yPct - 12)),
          w: 30, h: 25, material: null, trim: 'none',
        }] };
      })
    }));
    setPlacementMode(null);
  };

  const handleAddBench = (surfaceId, zoneId, xPct, yPct) => {
    updateSchedule(prev => ({
      ...prev,
      surfaces: prev.surfaces.map(s => {
        if (s.id !== surfaceId) return s;
        return { ...s, benches: [...(s.benches || []), {
          id: crypto.randomUUID(), zone_id: '_wall_level',
          x: Math.max(0, Math.min(60, xPct - 20)), y: 0,
          w: 40, h: 22, material: null, trim: 'none',
        }] };
      })
    }));
    setPlacementMode(null);
  };

  const handleCropConfirm = (crop) => {
    if (cropModal?.mode === 'place' && pendingPlaceRef.current) {
      const { surfaceId, zoneId, item } = pendingPlaceRef.current;
      placeItemWithCrop(surfaceId, zoneId, item, crop);
      pendingPlaceRef.current = null;
    } else if (cropModal?.mode === 'place_surface' && pendingPlaceRef.current) {
      const { surfaceId, item } = pendingPlaceRef.current;
      placeCeilingFloorWithCrop(surfaceId, item, crop);
      pendingPlaceRef.current = null;
    } else if (cropModal?.mode === 'edit' && cropModal.surfaceId && cropModal.materialId) {
      updateMaterialField(cropModal.surfaceId, cropModal.materialId, 'tile_crop', crop);
    }
    setCropModal(null);
  };

  const updateMaterialField = (surfaceId, materialId, field, value) => {
    updateSchedule(prev => ({
      ...prev,
      surfaces: prev.surfaces.map(s => {
        if (s.id !== surfaceId) return s;
        return { ...s, materials: s.materials.map(m => m.id === materialId ? { ...m, [field]: value } : m) };
      })
    }));
  };

  const handleCropTile = (surfaceId, materialId, imageUrl, existingCrop) => {
    setCropModal({ surfaceId, materialId, imageUrl, existingCrop: existingCrop || null, mode: 'edit' });
  };

  const removeMaterial = (surfaceId, materialId) => {
    updateSchedule(prev => ({
      ...prev,
      surfaces: prev.surfaces.map(s =>
        s.id === surfaceId ? { ...s, materials: s.materials.filter(m => m.id !== materialId) } : s
      )
    }));
  };

  const changePattern = (surfaceId, materialId, pattern) => updateMaterialField(surfaceId, materialId, 'pattern', pattern);
  const changeGrout = (surfaceId, materialId, color) => updateMaterialField(surfaceId, materialId, 'grout_color', color);
  const changeOrientation = (surfaceId, materialId, orientation) => updateMaterialField(surfaceId, materialId, 'tile_orientation', orientation);
  const changeTileScale = (surfaceId, materialId, scale) => updateMaterialField(surfaceId, materialId, 'tile_scale', scale);

  const handleDeleteElement = (surfaceId, type, elementId) => {
    updateSchedule(prev => ({
      ...prev,
      surfaces: prev.surfaces.map(s => {
        if (s.id !== surfaceId) return s;
        if (type === 'niche') return { ...s, niches: (s.niches || []).filter(n => n.id !== elementId) };
        if (type === 'bench') return { ...s, benches: (s.benches || []).filter(b => b.id !== elementId) };
        if (type === 'fixture') return { ...s, fixtures: (s.fixtures || []).filter(f => f.id !== elementId) };
        return s;
      })
    }));
    setSelectedElement(null);
  };

  const handleUpdateElement = (surfaceId, type, elementId, updates) => {
    updateSchedule(prev => ({
      ...prev,
      surfaces: prev.surfaces.map(s => {
        if (s.id !== surfaceId) return s;
        if (type === 'niche') return { ...s, niches: (s.niches || []).map(n => n.id === elementId ? { ...n, ...updates } : n) };
        if (type === 'bench') return { ...s, benches: (s.benches || []).map(b => b.id === elementId ? { ...b, ...updates } : b) };
        if (type === 'fixture') return { ...s, fixtures: (s.fixtures || []).map(f => f.id === elementId ? { ...f, ...updates } : f) };
        return s;
      })
    }));
  };

  const handleApplyTileToElement = (surfaceId, elementType, elementId, item) => {
    const existingCrop = findExistingCropForItem(item.id);
    const material = {
      image: item._img || '', pattern: 'stacked_horizontal',
      tile_crop: existingCrop || null, grout_color: '#4a4035',
      tile_orientation: 'horizontal', tile_scale: 1.0,
    };
    updateSchedule(prev => ({
      ...prev,
      surfaces: prev.surfaces.map(s => {
        if (s.id !== surfaceId) return s;
        if (elementType === 'niche') return { ...s, niches: (s.niches || []).map(n => n.id === elementId ? { ...n, material } : n) };
        if (elementType === 'bench') return { ...s, benches: (s.benches || []).map(b => b.id === elementId ? { ...b, material } : b) };
        return s;
      })
    }));
  };

  const handleDropOnWall = (wallKey, item) => {
    if (!activeSchedule || !item) return;
    const surfaces = activeSchedule.surfaces || [];
    item._img = item._img || getItemImage(item);
    const existingCrop = findExistingCropForItem(item.id);

    // Handle bench/niche drops
    if (wallKey === 'bench') {
      const backWall = surfaces.find(s => s.surface_type === 'wall' && s.name?.toLowerCase().includes('back')) || surfaces.find(s => s.surface_type === 'wall');
      if (backWall?.benches?.length) {
        handleApplyTileToElement(backWall.id, 'bench', backWall.benches[0].id, item);
      }
      return;
    }
    if (wallKey === 'niche') {
      const backWall = surfaces.find(s => s.surface_type === 'wall' && s.name?.toLowerCase().includes('back')) || surfaces.find(s => s.surface_type === 'wall');
      if (backWall?.niches?.length) {
        handleApplyTileToElement(backWall.id, 'niche', backWall.niches[0].id, item);
      }
      return;
    }

    let targetSurface;
    switch (wallKey) {
      case 'back_wall':
        targetSurface = surfaces.find(s => s.surface_type === 'wall' && s.name?.toLowerCase().includes('back')) || surfaces.find(s => s.surface_type === 'wall');
        break;
      case 'left_wall':
        targetSurface = surfaces.find(s => s.surface_type === 'wall' && s.name?.toLowerCase().includes('left'));
        break;
      case 'right_wall':
        targetSurface = surfaces.find(s => s.surface_type === 'wall' && s.name?.toLowerCase().includes('right'));
        break;
      case 'floor':
        targetSurface = surfaces.find(s => s.surface_type === 'floor');
        break;
      case 'ceiling':
        targetSurface = surfaces.find(s => s.surface_type === 'ceiling');
        break;
      default: return;
    }
    if (!targetSurface) return;
    if (targetSurface.surface_type === 'floor' || targetSurface.surface_type === 'ceiling') {
      placeCeilingFloorWithCrop(targetSurface.id, item, existingCrop);
    } else {
      // Place on THIS wall only (not auto-fill other walls)
      updateSchedule(prev => {
        const makeMat = (posLabel) => ({
          id: crypto.randomUUID(), item_id: item.id, name: item.name || '',
          vendor: item.vendor || '', sku: item.sku || '', size: item.size || '',
          color: item.finish_color || item.color || '',
          image: item._img || '', link: item.link || '', position_label: posLabel,
          tile_crop: existingCrop, grout_color: '#4a4035', tile_orientation: 'horizontal',
          tile_scale: 1.0, display_mode: displayMode || 'sheet',
          pattern: selectedPattern || 'stacked_horizontal',
        });
        return {
          ...prev,
          surfaces: prev.surfaces.map(s => {
            if (s.id !== targetSurface.id) return s;
            const zones = s.zone_config || DEFAULT_ZONE_CONFIG;
            let mats = [...(s.materials || [])];
            zones.forEach(z => {
              const idx = mats.findIndex(m => m.position_label === z.id);
              if (idx >= 0) mats[idx] = makeMat(z.id);
              else mats.push(makeMat(z.id));
            });
            return { ...s, materials: mats };
          })
        };
      });
    }
  };

  const handleEditDimension = (surfaceId, zoneId, value) => {
    updateSchedule(prev => ({
      ...prev,
      surfaces: prev.surfaces.map(s => {
        if (s.id !== surfaceId) return s;
        return { ...s, zone_config: (s.zone_config || DEFAULT_ZONE_CONFIG.map(z => ({ ...z }))).map(z =>
          z.id === zoneId ? { ...z, dimension: value } : z
        ) };
      })
    }));
  };

  const handleResizeZone = (surfaceId, newConfig) => {
    updateSchedule(prev => ({
      ...prev,
      surfaces: prev.surfaces.map(s =>
        s.id === surfaceId ? { ...s, zone_config: newConfig } : s
      )
    }));
  };

  const handleAddZone = (zoneType) => {
    updateSchedule(prev => ({
      ...prev,
      surfaces: prev.surfaces.map(s => {
        if (s.surface_type !== 'wall') return s;
        const zones = s.zone_config || DEFAULT_ZONE_CONFIG.map(z => ({ ...z }));
        if (zoneType === 'upper_accent' && !zones.find(z => z.id === 'upper_accent')) {
          const mainIdx = zones.findIndex(z => z.id === 'main_wall');
          if (mainIdx < 0) return s;
          const newZones = [...zones];
          newZones[mainIdx] = { ...newZones[mainIdx], height: newZones[mainIdx].height - 20 };
          newZones.splice(mainIdx, 0, { id: 'upper_accent', label: 'Upper', height: 20, dimension: '' });
          return { ...s, zone_config: newZones };
        }
        if (zoneType === 'wainscot' && !zones.find(z => z.id === 'wainscot')) {
          const mainIdx = zones.findIndex(z => z.id === 'main_wall');
          const floorIdx = zones.findIndex(z => z.id === 'floor');
          if (mainIdx < 0 || floorIdx < 0) return s;
          const newZones = [...zones];
          newZones[mainIdx] = { ...newZones[mainIdx], height: newZones[mainIdx].height - 15 };
          newZones.splice(floorIdx, 0, { id: 'wainscot', label: 'Wainscot', height: 15, dimension: '' });
          return { ...s, zone_config: newZones };
        }
        return s;
      })
    }));
  };

  const handleRemoveZone = (zoneType) => {
    updateSchedule(prev => ({
      ...prev,
      surfaces: prev.surfaces.map(s => {
        if (s.surface_type !== 'wall') return s;
        const zones = s.zone_config || DEFAULT_ZONE_CONFIG.map(z => ({ ...z }));
        const idx = zones.findIndex(z => z.id === zoneType);
        if (idx < 0) return s;
        const mainIdx = zones.findIndex(z => z.id === 'main_wall');
        if (mainIdx < 0) return s;
        const newZones = zones.filter(z => z.id !== zoneType);
        const mIdx = newZones.findIndex(z => z.id === 'main_wall');
        newZones[mIdx] = { ...newZones[mIdx], height: newZones[mIdx].height + zones[idx].height };
        return { ...s, zone_config: newZones, materials: (s.materials || []).filter(m => m.position_label !== zoneType) };
      })
    }));
  };

  if (loading) return <div className="text-center py-8 text-gray-500">Loading...</div>;



  const modeLabel = placementMode === 'niche' ? 'NICHE MODE: Click a wall zone to place a niche'
    : placementMode === 'bench' ? 'BENCH MODE: Click a wall zone to place a bench'
    : placementMode === 'fixture' ? `${(FIXTURE_CATEGORY_LABELS[fixtureCategory] || 'Fixture').toUpperCase()} MODE: select a product, then click a wall to place it`
    : selectedItem ? `${selectedItem.name} selected — now click the wall, floor or ceiling it goes on`
    : `Drag a finish onto a wall, or click one below then click the wall${roomName ? '' : ''}.`;

  const modeColor = placementMode === 'niche' ? 'text-cyan-400' : placementMode === 'bench' ? 'text-orange-400' : placementMode === 'fixture' ? 'text-blue-400' : selectedItem ? 'text-green-400 animate-pulse' : 'text-amber-400/80';

  return (
    <div data-testid="room-finish-schedule" style={{ background: '#1a1a24' }} className="rounded-xl">
      {cropModal && <TileCropModal imageUrl={cropModal.imageUrl} existingCrop={cropModal.existingCrop} onConfirm={handleCropConfirm} onCancel={() => { pendingPlaceRef.current = null; setCropModal(null); }} />}

      {/* MATERIAL PALETTE */}
      <div className="p-4 border-b border-white/10" style={{ background: 'linear-gradient(180deg, #222230 0%, #1a1a24 100%)' }}>
        <div className="flex justify-between items-center mb-3">
          <div>
            <h3 className="text-lg font-black text-white">{roomName} — Finish Schedule</h3>
            <div className={`text-xs font-bold mt-0.5 ${modeColor}`}>{modeLabel}</div>
          </div>
          <div className="flex gap-2">
            {(selectedItem || placementMode) && (
              <button onClick={() => { setSelectedItem(null); setPlacementMode(null); setSelectedElement(null); }}
                className="px-3 py-1.5 rounded text-xs font-bold text-red-400 border border-red-400/30" data-testid="deselect-btn">
                <X size={12} className="inline mr-1" />CANCEL
              </button>
            )}
            {onClose && <button onClick={onClose} className="px-3 py-1.5 rounded text-xs text-white/40 border border-white/10"><X size={14} /></button>}
          </div>
        </div>
        {roomItems.length === 0 ? (
          <div className="text-white/20 text-sm py-4 text-center">No items — add items via Checklist or FFE first</div>
        ) : (<>
          {/* Search the material library — paint by name or code, plus any
              imported wallpaper/tile. Picking one selects it for placement
              exactly as if it had been clicked in the palette. */}
          <div className="mb-2 max-w-md flex items-end gap-2">
            <div className="flex-1">
            <MaterialSearchField
              testId="finishes-material-search"
              placeholder="Search paint, wallpaper, tile… (e.g. Alabaster, SW 7005)"
              onSelect={(m) => {
                const picked = {
                  id: `lib-${m.manufacturer || ''}-${m.sku || m.name}`,
                  name: m.name,
                  vendor: m.manufacturer || '',
                  sku: m.sku || '',
                  size: m.size || '',
                  finish_color: m.color || '',
                  categoryName: m.category || 'Paint, Wallpaper & Tile',
                  source: 'Library',
                  _img: m.image_url || '',
                  _fromLibrary: true,
                };
                // Put it in the squares as well, so it can be dragged - not just
                // clicked - and stays there for the next wall.
                setSearchPicks(prev => (
                  prev.some(p => p.id === picked.id) ? prev : [picked, ...prev]
                ));
                setSelectedItem(picked);
                setSelectedElement(null);
                setPlacementMode(null);
              }}
            />
            </div>
            <button onClick={() => setShowLibrary(true)} data-testid="open-material-library"
              title="Load a supplier catalogue (wallpaper, tile, fabric) into the search"
              className="px-2 py-1.5 rounded text-[10px] font-black whitespace-nowrap text-white/60 border border-white/10 hover:text-white">
              📚 LIBRARY
            </button>
          </div>

          {/* Group filter — finishes first; only groups this room actually has */}
          <div className="flex gap-1 flex-wrap mb-2" data-testid="palette-groups">
            {availableGroups.map(g => (
              <button key={g.key} onClick={() => setPaletteGroup(g.key)}
                data-testid={`palette-group-${g.key}`}
                className={`px-2 py-1 rounded text-[9px] font-black ${activeGroup === g.key ? 'bg-amber-600 text-white' : 'text-white/40 border border-white/10 hover:text-white/70'}`}>
                {g.label} <span className="opacity-60">{(paletteBuckets.get(g.key) || []).length}</span>
              </button>
            ))}
            {availableGroups.length > 1 && (
              <button onClick={() => setPaletteGroup('all')}
                data-testid="palette-group-all"
                className={`px-2 py-1 rounded text-[9px] font-black ${activeGroup === 'all' ? 'bg-amber-600 text-white' : 'text-white/40 border border-white/10 hover:text-white/70'}`}>
                ALL <span className="opacity-60">{roomItems.length}</span>
              </button>
            )}
            {(paletteBuckets.get('plumbing') || []).length > 0 && activeSchedule && (
              <button onClick={placeAllPlumbing}
                data-testid="place-all-plumbing"
                title="Drop every plumbing fixture from FF&E/Checklist into this room's scene at once"
                className="px-2 py-1 rounded text-[9px] font-black bg-blue-600 text-white hover:bg-blue-500 flex items-center gap-1">
                🚿 PLACE ALL PLUMBING <span className="opacity-70">{(paletteBuckets.get('plumbing') || []).length}</span>
              </button>
            )}
          </div>
          <div className="flex gap-3 overflow-x-auto pb-2">
            <div className="flex-shrink-0 self-center text-[8px] text-white/30 font-bold px-1" style={{ writingMode: 'vertical-rl' }}>DRAG TO 3D</div>
            {itemsWithImages.map(item => (
              <button key={item.id} data-testid={`palette-item-${item.id}`}
                draggable
                onDragStart={(e) => {
                  e.dataTransfer.setData('application/json', JSON.stringify({
                    id: item.id, name: item.name, _img: item._img,
                    vendor: item.vendor || '', sku: item.sku || '', size: item.size || '',
                    finish_color: item.finish_color || '', color: item.color || '', link: item.link || '',
                  }));
                  e.dataTransfer.effectAllowed = 'copy';
                }}
                onClick={() => { setSelectedItem(selectedItem?.id === item.id ? null : item); setSelectedElement(null); setPlacementMode(null); }}
                className={`flex-shrink-0 rounded-lg overflow-hidden transition-all w-24 cursor-grab active:cursor-grabbing ${selectedItem?.id === item.id ? 'ring-3 ring-green-400 scale-105' : 'ring-1 ring-white/10 hover:ring-white/30'}`}>
                {item._img ? (
                  <img src={item._img} alt={item.name} className="w-full h-20 object-cover" draggable={false} />
                ) : (
                  /* Every item without a photo used to read "No Image", which
                     told you nothing and looked broken. Fall back to the item's
                     own finish colour, then to its initials. */
                  <div className="w-full h-20 flex flex-col items-center justify-center gap-1"
                    style={{ background: swatchColorFor(item) }}>
                    <span className="text-[15px] font-black" style={{ color: swatchTextFor(item) }}>
                      {initialsFor(item.name)}
                    </span>
                    <span className="text-[7px] uppercase tracking-wide" style={{ color: swatchTextFor(item), opacity: 0.7 }}>
                      {item.finish_color
                        ? item.finish_color.slice(0, 14)
                        : (isPaint(item) && item.sku ? item.sku.slice(0, 14) : 'no photo yet')}
                    </span>
                  </div>
                )}
                <div className="p-1" style={{ background: '#111' }}>
                  <div className="text-white text-[8px] font-black truncate">{item.name}</div>
                  <div className="text-white/40 text-[7px] truncate">{item.vendor} {item.size ? `| ${item.size}` : ''}{item.source ? ` • ${item.source}` : ''}</div>
                </div>
              </button>
            ))}
          </div>
        </>)}
      </div>

      {/* SCHEDULE CONTENT */}
      <div className="p-4">
        <div className="flex justify-between items-center mb-3 flex-wrap gap-2">
          <div className="flex gap-1 overflow-x-auto">
            {schedules.map(s => (
              <button key={s.id} onClick={() => { setActiveSchedule(s); setSelectedElement(null); }}
                className={`px-3 py-1.5 rounded text-xs font-bold whitespace-nowrap ${activeSchedule?.id === s.id ? 'bg-amber-600 text-white' : 'text-white/30 border border-white/10'}`}>{s.name}</button>
            ))}
          </div>
          <div className="flex gap-1 flex-wrap">
            <button onClick={() => setShowCreate(true)} className="px-3 py-1.5 rounded text-xs font-bold text-white bg-amber-700" data-testid="create-schedule-btn"><Plus size={12} className="inline mr-1" />NEW</button>
            {activeSchedule && <>
              <button onClick={() => {
                const backWall = activeSchedule.surfaces?.find(s => s.surface_type === 'wall' && s.name?.toLowerCase().includes('back'));
                if (backWall) handleAddNiche(backWall.id, 'main_wall', 50, 35);
              }}
                className="px-2 py-1.5 rounded text-[10px] font-bold flex items-center gap-1 text-white/30 border border-white/10 hover:bg-cyan-600 hover:text-white"
                data-testid="add-niche-btn"><Square size={10} />NICHE</button>
              <button onClick={() => {
                const backWall = activeSchedule.surfaces?.find(s => s.surface_type === 'wall' && s.name?.toLowerCase().includes('back'));
                if (backWall) handleAddBench(backWall.id, '_wall_level', 60, 0);
              }}
                className="px-2 py-1.5 rounded text-[10px] font-bold flex items-center gap-1 text-white/30 border border-white/10 hover:bg-orange-600 hover:text-white"
                data-testid="add-bench-btn"><GripVertical size={10} />BENCH</button>
              {FIXTURE_CATEGORIES.map(fc => {
                const active = placementMode === 'fixture' && fixtureCategory === fc.key;
                return (
                  <button key={fc.key}
                    onClick={() => {
                      if (active) { setPlacementMode(null); }
                      else { setFixtureCategory(fc.key); setPlacementMode('fixture'); }
                    }}
                    className="px-2 py-1.5 rounded text-[10px] font-bold flex items-center gap-1 border"
                    style={active ? { background: fc.color, color: '#fff', borderColor: fc.color } : { color: fc.color, borderColor: `${fc.color}55`, background: 'transparent' }}
                    data-testid={`add-${fc.key}-btn`}><Droplet size={10} />{fc.label.toUpperCase()}</button>
                );
              })}
              {(() => {
                const wallZones = (activeSchedule?.surfaces || []).find(s => s.surface_type === 'wall')?.zone_config || [];
                const hasUpper = wallZones.some(z => z.id === 'upper_accent');
                const hasWainscot = wallZones.some(z => z.id === 'wainscot');
                return <>
                  {!hasUpper ? <button onClick={() => handleAddZone('upper_accent')} className="px-2 py-1.5 rounded text-[10px] font-bold text-purple-300 border border-purple-400/20" data-testid="add-upper-btn"><Plus size={8} className="inline" /> UPPER</button>
                    : <button onClick={() => handleRemoveZone('upper_accent')} className="px-2 py-1.5 rounded text-[10px] font-bold text-purple-400 bg-purple-900/30 border border-purple-400/30" data-testid="remove-upper-btn"><X size={8} className="inline" /> UPPER</button>}
                  {!hasWainscot ? <button onClick={() => handleAddZone('wainscot')} className="px-2 py-1.5 rounded text-[10px] font-bold text-teal-300 border border-teal-400/20" data-testid="add-wainscot-btn"><Plus size={8} className="inline" /> WAINSCOT</button>
                    : <button onClick={() => handleRemoveZone('wainscot')} className="px-2 py-1.5 rounded text-[10px] font-bold text-teal-400 bg-teal-900/30 border border-teal-400/30" data-testid="remove-wainscot-btn"><X size={8} className="inline" /> WAINSCOT</button>}
                </>;
              })()}
              <button onClick={() => handleDelete(activeSchedule.id)} className="px-2 py-1.5 rounded text-[10px] text-red-400 border border-red-400/20"><Trash2 size={10} /></button>
            </>}
          </div>
        </div>

        {showCreate && (
          <div className="mb-3 p-3 rounded-lg border border-white/10" style={{ background: '#222' }}>
            <input type="text" data-testid="schedule-name-input" placeholder="Name (e.g. Primary Shower)" value={newName} onChange={e => setNewName(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') handleCreate(); }} className="w-full px-3 py-2 mb-2 rounded border border-white/20 bg-black text-white text-sm" />
            <div className="flex gap-2 mb-2">
              {SCHEDULE_TYPES.map(t => <button key={t.value} onClick={() => setNewType(t.value)} className={`px-3 py-1.5 rounded text-xs font-bold ${newType === t.value ? 'bg-amber-600 text-white' : 'text-white/30 border border-white/10'}`}>{t.label}</button>)}
            </div>
            <div className="flex gap-2">
              <button onClick={handleCreate} disabled={saving || !newName.trim()} className="px-4 py-1.5 rounded text-xs font-bold text-white bg-green-600 disabled:opacity-40">{saving ? '...' : 'CREATE'}</button>
              <button onClick={() => setShowCreate(false)} className="px-3 py-1.5 rounded text-xs text-white/40 border border-white/10">Cancel</button>
            </div>
          </div>
        )}

        {activeSchedule ? (
          <div>

            {/* 3D room preview — drag finishes from the palette onto surfaces, or
                switch the toolbar to OBJECT mode to place furniture, art and fixtures. */}
            <div ref={vizRef}>
            <RoomVisualizer3D
              schedule={activeSchedule}
              onDropTile={handleDropOnWall}
              onUpdateSchedule={updateSchedule}
              selectedItem={selectedItem}
            />
            </div>

            {/* Quick tile placement — apply selected tile to surfaces */}
            {selectedItem && (
              <div className="mt-2 p-2 bg-black/40 rounded-lg border border-white/10 space-y-2">
                {/* Display mode toggle */}
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-[9px] font-bold text-white/40">MODE:</span>
                  <button onClick={() => setDisplayMode('sheet')}
                    className={`px-2 py-1 rounded text-[9px] font-black ${displayMode === 'sheet' ? 'bg-amber-600 text-white' : 'text-white/40 border border-white/10'}`}
                    data-testid="mode-sheet">TILE</button>
                  <button onClick={() => setDisplayMode('full')}
                    className={`px-2 py-1 rounded text-[9px] font-black ${displayMode === 'full' ? 'bg-amber-600 text-white' : 'text-white/40 border border-white/10'}`}
                    data-testid="mode-full">FULL SHEET</button>
                  <span className="text-white/20 mx-1">|</span>
                  <span className="text-[9px] font-bold text-white/40">PATTERN:</span>
                  {[['stacked_horizontal','STACKED'],['offset','OFFSET'],['one_third_offset','1/3'],['stacked_vertical','VERTICAL'],['herringbone','HERRING'],['basket_weave','BASKET']].map(([p, label]) => (
                    <button key={p} onClick={() => { setSelectedPattern(p); updateAllWallMaterials({ pattern: p }); }}
                      className={`px-2 py-1 rounded text-[9px] font-black ${selectedPattern === p ? 'bg-cyan-600 text-white' : 'text-white/40 border border-white/10'}`}
                      data-testid={`pattern-${p}`}>{label}</button>
                  ))}
                </div>

                {/* Surface placement buttons */}
                <div className="flex gap-1 flex-wrap">
                  <span className="text-[9px] font-bold text-amber-400 self-center mr-1">APPLY &quot;{selectedItem.name}&quot;:</span>
                  <button onClick={() => {
                    const wallSurfaces = (activeSchedule.surfaces || []).filter(s => s.surface_type === 'wall');
                    const existingCrop = findExistingCropForItem(selectedItem.id);
                    if (existingCrop) { wallSurfaces.forEach(s => placeItemWithCrop(s.id, 'main_wall', selectedItem, existingCrop)); }
                    else if (selectedItem._img) { pendingPlaceRef.current = { surfaceId: wallSurfaces[0]?.id, zoneId: 'main_wall', item: selectedItem }; setCropModal({ imageUrl: selectedItem._img, existingCrop: null, mode: 'place' }); }
                  }} className="px-3 py-1 rounded text-[9px] font-black text-amber-300 bg-amber-900/30 hover:bg-amber-900/50 border border-amber-400/30"
                    data-testid="quick-place-all-walls">ALL WALLS</button>
                  {(activeSchedule.surfaces || []).filter(s => s.surface_type === 'wall').map(s => (
                    <button key={s.id} onClick={() => {
                      const existingCrop = findExistingCropForItem(selectedItem.id);
                      if (existingCrop) { placeItemWithCrop(s.id, 'main_wall', selectedItem, existingCrop); }
                      else if (selectedItem._img) { pendingPlaceRef.current = { surfaceId: s.id, zoneId: 'main_wall', item: selectedItem }; setCropModal({ imageUrl: selectedItem._img, existingCrop: null, mode: 'place' }); }
                    }} className="px-2 py-1 rounded text-[9px] font-bold text-white/60 bg-white/10 hover:bg-white/20 border border-white/10"
                      data-testid={`quick-place-${s.name}`}>{s.name}</button>
                  ))}
                  {activeSchedule.surfaces.filter(s => s.surface_type === 'floor').map(s => (
                    <button key={s.id} onClick={() => {
                      const existingCrop = findExistingCropForItem(selectedItem.id);
                      if (existingCrop) { placeCeilingFloorWithCrop(s.id, selectedItem, existingCrop); }
                      else if (selectedItem._img) { pendingPlaceRef.current = { surfaceId: s.id, zoneId: '_surface', item: selectedItem }; setCropModal({ imageUrl: selectedItem._img, existingCrop: null, mode: 'place_surface' }); }
                    }} className="px-2 py-1 rounded text-[9px] font-bold text-green-300/60 bg-green-900/20 hover:bg-green-900/40 border border-green-400/20"
                      data-testid="quick-place-floor">Floor</button>
                  ))}
                  {activeSchedule.surfaces.filter(s => s.surface_type === 'ceiling').map(s => (
                    <button key={s.id} onClick={() => {
                      const existingCrop = findExistingCropForItem(selectedItem.id);
                      if (existingCrop) { placeCeilingFloorWithCrop(s.id, selectedItem, existingCrop); }
                      else if (selectedItem._img) { pendingPlaceRef.current = { surfaceId: s.id, zoneId: '_surface', item: selectedItem }; setCropModal({ imageUrl: selectedItem._img, existingCrop: null, mode: 'place_surface' }); }
                    }} className="px-2 py-1 rounded text-[9px] font-bold text-purple-300/60 bg-purple-900/20 hover:bg-purple-900/40 border border-purple-400/20"
                      data-testid="quick-place-ceiling">Ceiling</button>
                  ))}
                </div>
              </div>
            )}

            {/* 2D Controls — hidden by default */}
            <details className="mt-2">
              <summary className="cursor-pointer text-xs font-bold text-white/30 px-2 py-1 bg-black/20 rounded hover:text-white/50">Click here to place tiles (2D Controls)</summary>
            <Shower3DView
              schedule={activeSchedule}
              selectedItem={selectedItem}
              placementMode={placementMode}
              selectedElement={selectedElement}
              onPlaceItem={handlePlaceItem}
              onPlaceFixture={handlePlaceFixture}
              onAddNiche={handleAddNiche}
              onAddBench={handleAddBench}
              onRemoveMaterial={removeMaterial}
              onChangePattern={changePattern}
              onCropTile={handleCropTile}
              onChangeGrout={changeGrout}
              onChangeOrientation={changeOrientation}
              onChangeTileScale={changeTileScale}
              onSelectElement={setSelectedElement}
              onDeleteElement={handleDeleteElement}
              onUpdateElement={handleUpdateElement}
              onApplyTileToElement={handleApplyTileToElement}
              onResizeZone={handleResizeZone}
              onEditDimension={handleEditDimension}
              onPlaceCeilingFloor={handlePlaceCeilingFloor}
              onAddZone={handleAddZone}
              onRemoveZone={handleRemoveZone}
            />
            </details>

            <div className="mt-3 flex gap-1 flex-wrap">
              {activeSchedule.surfaces.map(s => (
                <span key={s.id} className="px-2 py-1 rounded text-[8px] font-bold text-white/40 bg-white/5 flex items-center gap-1">
                  <span className={s.surface_type === 'ceiling' ? 'text-purple-300' : s.surface_type === 'floor' ? 'text-green-300' : 'text-white/40'}>{s.name}</span>
                </span>
              ))}
            </div>
          </div>
        ) : schedules.length === 0 ? (
          /* The room is built automatically on open, so getting here means that
             failed - say so and offer a retry rather than blaming the user. */
          <div className="text-center py-12">
            <div className="text-white/40 text-lg font-bold mb-2">Could not build this room</div>
            <div className="text-white/25 text-sm mb-4">
              The 3D room is created for you when you open it. Something went wrong this time.
            </div>
            <button
              onClick={() => { autoCreatedRef.current = false; fetchSchedules(); }}
              data-testid="retry-build-room"
              className="px-4 py-2 rounded text-xs font-bold text-white bg-amber-700"
            >
              Try again
            </button>
          </div>
        ) : null}
      </div>

      {showLibrary && (
        <MaterialLibraryManager onClose={() => setShowLibrary(false)} />
      )}
    </div>
  );
};

export default RoomFinishSchedule;
